<template>
  <div class="snake-map-dashboard">
    <PageLoader :storage="appLoading" />
    <!-- ENGAGING ANALYTICS DATA LOADER OVERLAY -->
    <transition name="fade">
      <div v-if="appLoading" class="analytics-loading-overlay">
        <div class="analytics-loader-card">
          <!-- Radar Pulse animation -->
          <div class="radar-pulse-container">
            <div class="radar-ring ring-1"></div>
            <div class="radar-ring ring-2"></div>
            <div class="radar-ring ring-3"></div>
            <div class="loader-center-icon">
              <v-icon large color="#0f766e">mdi-map-marker-radius-outline</v-icon>
            </div>
          </div>

          <h3 class="loader-heading">INITIALIZING GEOSPATIAL ANALYTICS</h3>
          <p class="loader-subtext">Fetching & indexing historical SARPA rescue encounters...</p>

          <!-- Animated Progress Line -->
          <div class="loader-progress-wrap">
            <div class="loader-progress-bar"></div>
          </div>

          <!-- Dynamic Rotating Status Messages -->
          <div class="loader-status-ticker">
            <v-icon x-small color="#0f766e" class="mr-1 spinning-icon">mdi-loading</v-icon>
            <span class="ticker-text">{{ currentLoadingMessage }}</span>
          </div>

          <div class="loader-footer-hint">
            <v-icon x-small color="#64748b" class="mr-1">mdi-database-sync-outline</v-icon>
            Optimizing Leaflet Canvas Engine for 80,000+ records
          </div>
        </div>
      </div>
    </transition>
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showSnackBar" color="primary" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span class="white--text">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon color="#fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Map Element (taking full viewport content size) -->
    <div id="map" ref="mapContainer"></div>

    <!-- Floating Title Banner -->
    <div class="floating-title-banner hidden-xs-only">
      <div class="banner-main-title">GEOSPATIAL & AI PREDICTIVE ANALYTICS</div>
      <div class="banner-sub-info">
        <span class="banner-subtitle">Snake Reports & Rescues</span>
        <v-chip x-small color="#0f766e" class="white--text font-weight-black ml-2" label>
          Loaded: {{ totalcount }}
        </v-chip>
      </div>
    </div>

    <!-- Floating Compass/North Arrow (top-right) -->
    <div class="floating-north-arrow">
      <v-btn fab small color="white" depressed readonly style="pointer-events: none;">
        <v-icon color="#0f766e">mdi-navigation</v-icon>
      </v-btn>
    </div>

    <!-- Floating Scarcity Map Action Button -->
    <div class="floating-scarcity-btn-container hidden-xs-only">
      <v-btn
        :color="showShortageMapOverlay ? 'red darken-2' : 'white'"
        :class="showShortageMapOverlay ? 'white--text elevation-8 pulsate-scarcity' : 'red--text text--darken-2 elevation-4'"
        rounded
        small
        class="font-weight-black"
        @click="toggleShortageMapOverlay"
      >
        <v-icon left :color="showShortageMapOverlay ? 'white' : 'red darken-2'">
          {{ showShortageMapOverlay ? 'mdi-map-marker-alert' : 'mdi-account-alert-outline' }}
        </v-icon>
        {{ showShortageMapOverlay ? 'HIDE SCARCITY ZONES' : 'PREDICT RESCUER SCARCITY' }}
      </v-btn>
    </div>

    <!-- Left Vertical Floating Toolbar -->
    <div class="map-floating-toolbar">
      <v-btn
        fab
        small
        :color="activePanel === 'filter' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('filter')"
        title="Query Filters"
      >
        <v-icon :color="activePanel === 'filter' ? 'white' : '#0f766e'">mdi-filter-menu-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'layers' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('layers')"
        title="Map Overlays & GIS Layers"
      >
        <v-icon :color="activePanel === 'layers' ? 'white' : '#0f766e'">mdi-layers-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'predictive' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('predictive')"
        title="AI Predictive Analytics"
      >
        <v-icon :color="activePanel === 'predictive' ? 'white' : '#0f766e'">mdi-robot</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'spatial' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('spatial')"
        title="Spatiotemporal Encounter Forecast"
      >
        <v-icon :color="activePanel === 'spatial' ? 'white' : '#0f766e'">mdi-crystal-ball</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'ask_ai' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('ask_ai')"
        title="Ask SARPA AI Assistant"
      >
        <v-icon :color="activePanel === 'ask_ai' ? 'white' : '#0f766e'">mdi-head-question-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'ops' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('ops')"
        title="AI Ops Center"
      >
        <v-icon :color="activePanel === 'ops' ? 'white' : '#0f766e'">mdi-brain</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="exportMapPDF"
        :loading="exportLoading"
        title="Export Map as PDF"
      >
        <v-icon color="#e11d48">mdi-file-pdf-box</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="exportMapPNG"
        :loading="exportLoading"
        title="Export Map as PNG"
      >
        <v-icon color="#2563eb">mdi-file-image-outline</v-icon>
      </v-btn>

      <v-btn
        v-if="canExportExcel"
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="exportMapExcel"
        :loading="exportLoading"
        title="Export Current Data as Excel"
      >
        <v-icon color="#15803d">mdi-microsoft-excel</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="activePanel === 'stats' ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="togglePanel('stats')"
        title="Spatial Selection Analytics"
      >
        <v-icon :color="activePanel === 'stats' ? 'white' : '#0f766e'">mdi-chart-box-outline</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        :color="showDrawControls ? '#0f766e' : 'white'"
        class="toolbar-btn"
        @click="toggleDrawControls"
        title="Toggle Draw Tools"
      >
        <v-icon :color="showDrawControls ? 'white' : '#0f766e'">mdi-vector-polygon</v-icon>
      </v-btn>

      <v-btn
        fab
        small
        color="white"
        class="toolbar-btn"
        @click="toggleFullscreen"
        title="Fullscreen Mode"
      >
        <v-icon color="#0f766e">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </v-btn>
    </div>

    <!-- Floating Panels Container -->
    <transition name="slide-fade">
      <!-- Filter Panel Overlay -->
      <v-card v-if="activePanel === 'filter'" key="card-filter" class="floating-overlay-card filter-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-4">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3">Query Criteria</h3>
            <p class="panel-card-subtitle ma-0">Filter snake incidents by categories and locations.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content">
          <!-- Checkbox Toggles -->
          <div class="checkbox-grid mb-3">
            <v-checkbox
              v-model="alldata"
              label="All Records"
              @change="checkUpdate()"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
            <v-checkbox
              v-model="timeperiod"
              label="Time Period"
              :disabled="alldata"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
            <v-checkbox
              v-model="bigsnakes"
              label="Big 4 Snakes"
              :disabled="alldata"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
            <v-checkbox
              v-model="allsnakes"
              label="All Species"
              :disabled="alldata"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
            <v-checkbox
              v-model="users"
              label="Rescuer User"
              :disabled="alldata"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
            <v-checkbox
              v-model="divisioncheck"
              label="Office (Div/Range)"
              :disabled="alldata"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
            <v-checkbox
              v-model="districtcheck"
              label="District"
              :disabled="alldata"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 mb-1"
            ></v-checkbox>
          </div>

          <v-divider class="mb-3"></v-divider>

          <!-- Input Fields Form -->
          <div class="filters-form-scroll" v-if="!alldata">
            <!-- Date fields -->
            <div class="mb-3" v-if="timeperiod">
              <span class="field-label mb-1 d-block font-weight-bold">From Date</span>
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="fromdate"
                    append-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    color="#0f766e"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="fromdate"
                  @change="save"
                  color="#0f766e"
                ></v-date-picker>
              </v-menu>
            </div>

            <div class="mb-3" v-if="timeperiod">
              <span class="field-label mb-1 d-block font-weight-bold">To Date</span>
              <v-menu
                v-model="menu2"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="todate"
                    append-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    color="#0f766e"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="todate"
                  @change="saveto"
                  color="#0f766e"
                ></v-date-picker>
              </v-menu>
            </div>

            <!-- Big 4 Selection -->
            <div class="mb-3" v-if="bigsnakes">
              <span class="field-label mb-1 d-block font-weight-bold">Select Big 4</span>
              <v-select
                :items="bigsnakestypes"
                v-model="bigsnakeselect"
                item-text="text"
                item-value="value"
                multiple
                outlined
                dense
                hide-details
                color="#0f766e"
                item-color="#0f766e"
              ></v-select>
            </div>

            <!-- All Snakes Selection -->
            <div class="mb-3" v-if="allsnakes">
              <span class="field-label mb-1 d-block font-weight-bold">Select Species</span>
              <v-select
                :items="snakeitems"
                v-model="allsnakelist"
                multiple
                item-text="name"
                item-value="_id"
                outlined
                dense
                hide-details
                color="#0f766e"
                item-color="#0f766e"
              ></v-select>
            </div>

            <!-- Users Autocomplete -->
            <div class="mb-3" v-if="users">
              <span class="field-label mb-1 d-block font-weight-bold">Select User</span>
              <v-autocomplete
                :items="userslist"
                v-model="selecteduser"
                :search-input.sync="searchUser"
                item-text="username"
                item-value="username"
                clearable
                outlined
                dense
                hide-details
                color="#0f766e"
              ></v-autocomplete>
            </div>

            <!-- Division Name -->
            <div class="mb-3" v-if="divisioncheck">
              <span class="field-label mb-1 d-block font-weight-bold">Division</span>
              <v-autocomplete
                outlined
                :items="divisionList"
                v-model="divisionname"
                item-text="divisionName"
                item-value="_id"
                dense
                hide-details
                color="#0f766e"
              ></v-autocomplete>
            </div>

            <!-- Range Name -->
            <div class="mb-3" v-if="divisioncheck">
              <span class="field-label mb-1 d-block font-weight-bold">Range</span>
              <v-autocomplete
                outlined
                :items="rangeList"
                v-model="rangename"
                item-text="rangeName"
                item-value="_id"
                dense
                hide-details
                color="#0f766e"
                :disabled="!divisionname"
              ></v-autocomplete>
            </div>

            <!-- District Selection -->
            <div class="mb-3" v-if="districtcheck">
              <span class="field-label mb-1 d-block font-weight-bold">District</span>
              <v-select
                :items="districtList"
                v-model="district"
                outlined
                dense
                hide-details
                color="#0f766e"
                item-color="#0f766e"
              ></v-select>
            </div>
          </div>

          <!-- Empty state when alldata -->
          <div v-else class="alldata-banner pa-3 mb-4 text-center rounded-lg">
            <v-icon small color="#0f766e" class="mr-1">mdi-information-outline</v-icon>
            <span class="caption teal--text text--darken-3 font-weight-bold">Displaying all global records. No filters active.</span>
          </div>

          <!-- Action buttons -->
          <div class="d-flex align-center gap-2 mt-2 pt-2">
            <v-btn v-if="!alldata" class="flex-grow-1 white--text" color="#0f766e" depressed @click="find()">Submit</v-btn>
            <v-btn v-if="!alldata" class="flex-grow-1" outlined color="#0f766e" depressed @click="reset()">Reset</v-btn>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- Layers & GIS Overlays Card -->
      <v-card v-if="activePanel === 'layers'" key="card-layers" class="floating-overlay-card layers-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-4">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3">Map Overlays & GIS Layers</h3>
            <p class="panel-card-subtitle ma-0">Configure data visualization & spatial boundary layers.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content">
          <div class="stats-label mb-2 font-weight-bold text-uppercase teal--text text--darken-4" style="font-size:11px;">Data Displays</div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="showPointers"
              label="Individual Pointers"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleMapMode"
            ></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="showHotspots"
              label="Cluster Hotspots"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleMapMode"
            ></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-3 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="showHeatmap"
              label="Predictive Risk Heatmap"
              color="#d97706"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleMapMode"
            ></v-checkbox>
          </div>

          <v-divider class="my-3"></v-divider>

          <div class="stats-label mb-2 font-weight-bold text-uppercase teal--text text--darken-4" style="font-size:11px;">GIS Boundary Layers</div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="gisLayers.districts"
              label="District Boundaries"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleDistrictLayer"
            ></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="gisLayers.divisions"
              label="Forest Divisions"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleDivisionLayer"
            ></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="gisLayers.ranges"
              label="Forest Ranges"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleRangeLayer"
            ></v-checkbox>
          </div>
          <div class="layer-option-row pa-2 mb-2 d-flex align-center rounded-lg">
            <v-checkbox
              v-model="gisLayers.pas"
              label="Protected Areas (PAs)"
              color="#0f766e"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="togglePASLayer"
            ></v-checkbox>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- ASK SARPA AI ASSISTANT CARD -->
      <v-card v-if="activePanel === 'ask_ai'" key="card-ask-ai" class="floating-overlay-card ask-ai-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-3">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3 d-flex align-center">
              <v-icon color="#0f766e" class="mr-1">mdi-head-question-outline</v-icon>
              Ask SARPA AI Assistant
            </h3>
            <p class="panel-card-subtitle ma-0">District Spatial Intelligence & Rescuer Allocation Query.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <!-- QUICK QUERY CHIPS -->
          <div class="caption font-weight-black grey--text text--darken-3 mb-1">Quick District Intelligence Query</div>
          <div class="d-flex flex-wrap gap-1 mb-3">
            <v-chip
              v-for="dName in [
                'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
                'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
                'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasaragod'
              ]"
              :key="'chip-' + dName"
              x-small
              outlined
              color="#0f766e"
              class="font-weight-bold ma-1"
              @click="runDistrictAIQuery(dName)"
            >
              {{ dName }}
            </v-chip>
          </div>

          <!-- QUERY INPUT FIELD -->
          <v-text-field
            v-model="aiQueryInputText"
            placeholder="Ask a question (e.g. In Idukki do we need more rescuers?)"
            outlined
            dense
            hide-details
            append-icon="mdi-send"
            color="#0f766e"
            class="mb-3"
            @keyup.enter="handleAIQuerySubmit"
            @click:append="handleAIQuerySubmit"
          ></v-text-field>

          <!-- QUERY RESULT DISPLAY -->
          <div v-if="aiQueryResult" class="ai-query-result-box pa-3 rounded-lg" style="background: #f0fdf4; border: 1px solid #bbf7d0;">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-black text-uppercase caption teal--text text--darken-4">
                📍 {{ aiQueryResult.districtName }} Intelligence Report
              </span>
              <v-chip x-small :color="aiQueryResult.shortageNeeded > 0 ? 'red darken-2' : 'teal darken-2'" class="white--text font-weight-black">
                {{ aiQueryResult.shortageStatus }}
              </v-chip>
            </div>

            <!-- RESCUER RECOMMENDATION & EXPLANATION -->
            <div class="pa-2 rounded mb-3" style="background: #ffffff; border: 1px solid #d1fae5;">
              <div class="caption font-weight-black text-uppercase grey--text text--darken-3 mb-1">
                🚨 Rescuer Deployment Need
              </div>
              <p class="caption font-weight-bold mb-2" style="color: #0f766e;">
                {{ aiQueryResult.rescuerAdvice }}
              </p>

              <!-- SLA & BACKLOG METRICS STRIP -->
              <div class="d-flex mb-2" style="gap: 6px;">
                <div class="pa-2 rounded flex-grow-1" style="background: #fdf2f8; border: 1px solid #fbcfe8;">
                  <div class="caption grey--text text--darken-2" style="font-size: 10px;">Avg Response SLA</div>
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #be185d;">
                    ⏱️ {{ aiQueryResult.avgResponseMins }} Mins Arrival
                  </div>
                </div>
                <div class="pa-2 rounded flex-grow-1" style="background: #fff7ed; border: 1px solid #ffedd5;">
                  <div class="caption grey--text text--darken-2" style="font-size: 10px;">Unattended Backlog</div>
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #c2410c;">
                    ⚠️ {{ aiQueryResult.unattendedCount }} Pending ({{ aiQueryResult.unattendedRate }}%)
                  </div>
                </div>
              </div>

              <!-- WHY THIS RECOMMENDATION WAS GENERATED -->
              <div class="pa-2 rounded" style="background: #f8fafc; border-left: 3px solid #0f766e; font-size: 11px;">
                <div class="font-weight-black grey--text text--darken-4 mb-1 d-flex align-center">
                  <v-icon small color="#0f766e" class="mr-1">mdi-information-outline</v-icon>
                  Why this recommendation was displayed:
                </div>
                <div class="caption grey--text text--darken-3">
                  <div>• <strong>Encounter Volume:</strong> {{ aiQueryResult.totalCalls.toLocaleString() }} total rescue calls logged in {{ aiQueryResult.districtName }}.</div>
                  <div>• <strong>State Load Concentration:</strong> Represents <strong>{{ aiQueryResult.stateShare }}%</strong> of Kerala's total encounter workload.</div>
                  <div>• <strong>Response Time SLA:</strong> Average arrival time is <strong>{{ aiQueryResult.avgResponseMins }} mins</strong> ({{ aiQueryResult.slaStatus }}).</div>
                  <div>• <strong>Unattended Backlog Index:</strong> <strong>{{ aiQueryResult.unattendedCount.toLocaleString() }} calls</strong> currently pending rescuer response ({{ aiQueryResult.unattendedRate }}% backlog).</div>
                  <div>• <strong>Venom Threat Level:</strong> <strong>{{ aiQueryResult.venomPercent }}%</strong> of encounters involve Highly Venomous species (Cobra/Viper).</div>
                  <div>• <strong>Deployment Formula Derivation:</strong> {{ aiQueryResult.rescuerFormula }}</div>
                </div>
              </div>
            </div>

            <!-- SPECIES PROBABILITY CHANCE BREAKDOWN -->
            <div class="caption font-weight-black grey--text text--darken-3 mb-1">
              🐍 Species Encounter Chances in {{ aiQueryResult.districtName }}
            </div>
            <div v-for="sp in aiQueryResult.speciesChances" :key="'ai-sp-' + sp.name" class="mb-2">
              <div class="d-flex justify-space-between caption font-weight-bold" style="font-size: 10px;">
                <span>{{ sp.name }}</span>
                <span class="teal--text text--darken-3 font-weight-black">{{ sp.chance }}% Chance</span>
              </div>
              <v-progress-linear :value="sp.chance" height="6" rounded color="#0f766e"></v-progress-linear>
            </div>

            <!-- SUB-DISTRICT AREA PREDICTION & RESCUER NEED BREAKDOWN -->
            <div v-if="aiQueryResult.districtAreasBreakdown && aiQueryResult.districtAreasBreakdown.length > 0" class="mt-3">
              <div class="caption font-weight-black grey--text text--darken-3 mb-1">
                📍 Predicted High-Risk Areas inside {{ aiQueryResult.districtName }}
              </div>
              <div
                v-for="area in aiQueryResult.districtAreasBreakdown"
                :key="'ai-area-' + area.areaName"
                class="pa-2 rounded mb-2"
                style="background: #f8fafc; border: 1px solid #cbd5e1;"
              >
                <div class="d-flex justify-space-between align-center">
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #0f766e; max-width: 160px;">
                    {{ area.areaName }}
                  </div>
                  <v-chip x-small :color="area.needed > 0 ? 'red darken-2' : 'teal darken-2'" class="white--text font-weight-black">
                    {{ area.needed > 0 ? '+' + area.needed + ' Needed' : 'Optimal' }}
                  </v-chip>
                </div>
                <div class="caption grey--text text--darken-2" style="font-size: 10px;">
                  {{ area.share }}% Load ({{ area.count.toLocaleString() }} calls) • Top: {{ area.topSpecies }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="caption text-center grey--text text--darken-1 py-4">
            Select a district chip above or type a question to generate a real-time spatial intelligence report.
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- AI PREDICTIVE ANALYTICS OVERLAY CARD -->
      <v-card v-if="activePanel === 'predictive'" key="card-predictive" class="floating-overlay-card predictive-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-3">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3 d-flex align-center">
              <v-icon color="#0f766e" class="mr-1">mdi-robot</v-icon>
              AI Predictive Encounter Model
            </h3>
            <p class="panel-card-subtitle ma-0">Spatial risk classifier & species threat analysis.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <!-- SEASONAL TIME-SERIES PREDICTION SIMULATOR -->
          <div class="seasonal-simulator-wrap mb-4 pa-2 rounded-lg" style="background: #f1f5f9; border: 1px solid #e2e8f0;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-1 d-flex align-center">
              <v-icon small color="#0f766e" class="mr-1">mdi-calendar-clock</v-icon>
              SEASONAL TIME-SERIES FORECAST SIMULATOR
            </div>
            <v-chip-group v-model="selectedSeason" active-class="teal darken-3 white--text" mandatory dense class="ma-0">
              <v-chip filter x-small value="all">All Year</v-chip>
              <v-chip filter x-small value="current">Current Month</v-chip>
              <v-chip filter x-small value="monsoon">Monsoon (Jun-Sep)</v-chip>
              <v-chip filter x-small value="post_monsoon">Post-Monsoon (Oct-Dec)</v-chip>
              <v-chip filter x-small value="summer">Summer (Jan-May)</v-chip>
            </v-chip-group>
          </div>

          <!-- ENCOUNTER TIME-SERIES FORECAST CHART -->
          <div class="forecast-chart-wrap mb-4 pa-3 rounded-lg" style="background: #ffffff; border: 1px solid #e2e8f0;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-2 d-flex align-center">
              <v-icon small color="#0f766e" class="mr-1">mdi-chart-timeline-variant</v-icon>
              ENCOUNTER FORECAST · 12-MONTH HISTORY + 3-MONTH PROJECTION
            </div>

            <div v-if="forecastBars.length > 0">
              <div class="forecast-bars d-flex align-end" style="height: 88px; gap: 3px;">
                <div
                  v-for="(bar, i) in forecastBars"
                  :key="'fbar-' + i"
                  class="forecast-bar flex-grow-1"
                  :title="bar.label + ': ' + bar.count + (bar.projected ? ' (projected)' : '')"
                  :style="{
                    height: bar.displayPct + '%',
                    borderRadius: '3px 3px 0 0',
                    background: bar.projected
                      ? 'repeating-linear-gradient(45deg, #f59e0b, #f59e0b 3px, #fcd34d 3px, #fcd34d 6px)'
                      : '#0f766e',
                    opacity: bar.projected ? 0.95 : 1,
                  }"
                ></div>
              </div>
              <div class="d-flex justify-space-between mt-1">
                <span class="grey--text text--darken-1" style="font-size: 9px;">{{ forecastBars[0].label }}</span>
                <span class="grey--text text--darken-1" style="font-size: 9px;">{{ forecastBars[forecastBars.length - 1].label }}</span>
              </div>
              <div class="d-flex align-center mt-2" style="gap: 10px;">
                <span class="d-flex align-center" style="font-size: 10px;">
                  <span style="width: 10px; height: 10px; background: #0f766e; border-radius: 2px; display: inline-block;" class="mr-1"></span>
                  Historical
                </span>
                <span class="d-flex align-center" style="font-size: 10px;">
                  <span style="width: 10px; height: 10px; background: repeating-linear-gradient(45deg, #f59e0b, #f59e0b 2px, #fcd34d 2px, #fcd34d 4px); border-radius: 2px; display: inline-block;" class="mr-1"></span>
                  Projected
                </span>
              </div>
              <p class="caption grey--text text--darken-2 ma-0 mt-2" style="font-size: 10px;">
                {{ predictiveData && predictiveData.forecast ? predictiveData.forecast.insight : '' }}
              </p>
            </div>
            <div v-else class="caption grey--text text--darken-1 text-center py-3">
              Not enough historical data to project a forecast.
            </div>
          </div>

          <!-- RESCUER LOAD-BALANCE FORECAST MATRIX -->
          <div class="rescuer-shortage-section mb-4 pa-3 rounded-lg" style="background: #fff5f5; border: 1px solid #fecdd3;">
            <div class="caption font-weight-black red--text text--darken-4 mb-1 d-flex align-center justify-space-between">
              <span><v-icon small color="#e11d48" class="mr-1">mdi-account-alert-outline</v-icon> RESCUER LOAD-BALANCE FORECAST</span>
              <v-chip x-small color="red" class="white--text font-weight-black">
                {{ deepPredictiveStats.shortageCount }} Above Avg
              </v-chip>
            </div>
            <p class="caption grey--text text--darken-2 mb-2" style="font-size: 10px;">
              Districts carrying more incidents per rescuer than the state average
              <span v-if="predictiveData && predictiveData.totals">({{ predictiveData.totals.benchmarkIncidentsPerRescuer }}/rescuer benchmark)</span>.
            </p>

            <!-- MAP OVERLAY CHECKBOX TOGGLE -->
            <div class="pa-2 rounded mb-3 d-flex align-center" style="background: #ffe4e6; border: 1px solid #fca5a5;">
              <v-checkbox
                v-model="showShortageMapOverlay"
                label="Highlight Deficit Zones on Map"
                color="#e11d48"
                hide-details
                dense
                class="ma-0 pa-0 font-weight-black red--text text--darken-4"
                style="font-size: 11px;"
                @change="toggleShortageLayer"
              ></v-checkbox>
            </div>

            <div class="shortage-tiles-list" v-if="deepPredictiveStats.shortageDivisions.length > 0">
              <div
                v-for="item in deepPredictiveStats.shortageDivisions"
                :key="'shortage-' + item.name"
                class="shortage-tile pa-2 rounded-lg mb-2 d-flex align-center justify-space-between"
                style="background: #ffffff; border: 1px solid #ffe4e6;"
              >
                <div>
                  <div class="font-weight-black text-truncate" style="font-size: 11px; color: #1e293b; max-width: 170px;">
                    {{ item.name }}
                  </div>
                  <div class="caption grey--text text--darken-2" style="font-size: 10px;">
                    {{ item.count.toLocaleString() }} incidents • {{ item.activeRescuers }} rescuers • {{ item.incidentsPerRescuer }}/rescuer
                    <span v-if="item.loadIndex" class="red--text text--darken-2 font-weight-black">· {{ item.loadIndex }}× avg load</span>
                  </div>
                </div>
                <v-chip x-small :color="item.loadIndex >= 2 ? 'red darken-2' : item.loadIndex >= 1.5 ? 'deep-orange darken-2' : 'amber darken-3'" class="white--text font-weight-black">
                  +{{ item.needed }} to balance
                </v-chip>
              </div>
            </div>
            <div v-else class="caption grey--text text--darken-1 text-center py-1">
              No critical rescuer capacity shortages detected for this temporal window.
            </div>
          </div>

          <!-- SPECIES SPATIAL PROBABILITY & HABITAT CHANCE PREDICTOR -->
          <div class="species-probability-section mb-4 pa-3 rounded-lg" style="background: #f8fafc; border: 1px solid #cbd5e1;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-2 d-flex align-center">
              <v-icon small color="#0f766e" class="mr-1">mdi-target</v-icon>
              SPECIES HABITAT CHANCE PREDICTOR
            </div>
            <v-select
              v-model="selectedProbabilitySpecies"
              :items="availableSpeciesList"
              label="Select Species to Predict"
              outlined
              dense
              clearable
              hide-details
              color="#0f766e"
              class="mb-3"
              :menu-props="{ offsetY: true, zIndex: 100000, contentClass: 'sarpa-panel-menu' }"
            ></v-select>

            <div class="caption font-weight-bold grey--text text--darken-2 mb-1">District Spatial Probability Index</div>
            <div v-for="spItem in speciesProbabilityBreakdown" :key="'prob-' + spItem.district" class="mb-2">
              <div class="d-flex justify-space-between caption font-weight-bold" style="font-size: 10px;">
                <span>{{ spItem.district }} ({{ spItem.speciesName }})</span>
                <span class="teal--text text--darken-3 font-weight-black">{{ spItem.chance }}% Chance</span>
              </div>
              <v-progress-linear :value="spItem.chance" height="6" rounded color="#0f766e"></v-progress-linear>
            </div>
          </div>

          <!-- SEASONAL SPECIES ACTIVITY SURGE -->
          <div class="species-surge-section mb-4 pa-3 rounded-lg" style="background: #f0fdf4; border: 1px solid #bbf7d0;">
            <div class="d-flex align-center mb-1">
              <v-icon small color="#0f766e" class="mr-1">mdi-trending-up</v-icon>
              <strong style="font-size:11px; color: #0f766e;" class="text-uppercase">
                SEASONAL SURGE: {{ deepPredictiveStats.topSurgeSpecies }}
              </strong>
            </div>
            <p class="caption text--secondary ma-0" style="font-size:11px;">
              {{ deepPredictiveStats.surgeInsight }}
            </p>
          </div>

          <!-- Predictive Summary Grid -->
          <div class="predictive-metrics-grid mb-3">
            <div class="metric-box bg-teal-light">
              <span class="metric-box__label">Seasonal Encounters</span>
              <strong class="metric-box__val teal--text text--darken-4">{{ deepPredictiveStats.seasonalEncountersCount }}</strong>
            </div>
            <div class="metric-box bg-rose-light">
              <span class="metric-box__label">Highly Venomous</span>
              <strong class="metric-box__val red--text text--darken-3">{{ predictiveStats.highlyVenomousCount }}</strong>
            </div>
            <div class="metric-box bg-amber-light">
              <span class="metric-box__label">Mildly Venomous</span>
              <strong class="metric-box__val amber--text text--darken-4">{{ predictiveStats.mildlyVenomousCount }}</strong>
            </div>
            <div class="metric-box bg-blue-light">
              <span class="metric-box__label">Non-Venomous</span>
              <strong class="metric-box__val blue--text text--darken-4">{{ predictiveStats.nonVenomousCount }}</strong>
            </div>
          </div>

          <!-- Risk Category Breakdown -->
          <div class="stats-label mb-2 font-weight-black grey--text text--darken-3">Species Threat Classification</div>
          <div class="mb-3">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span>Highly Venomous (Big 4 / Cobra / Viper / Krait)</span>
              <span class="red--text text--darken-2 font-weight-black">{{ predictiveStats.highlyVenomousCount }} ({{ predictiveStats.venomRatio }}%)</span>
            </div>
            <v-progress-linear :value="predictiveStats.total > 0 ? (predictiveStats.highlyVenomousCount / predictiveStats.total) * 100 : 0" height="8" rounded color="#e11d48"></v-progress-linear>
          </div>

          <div class="mb-3">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span>Mildly Venomous (Vine / Pit Viper / Cat Snake)</span>
              <span class="amber--text text--darken-3 font-weight-black">{{ predictiveStats.mildlyVenomousCount }}</span>
            </div>
            <v-progress-linear :value="predictiveStats.total > 0 ? (predictiveStats.mildlyVenomousCount / predictiveStats.total) * 100 : 0" height="8" rounded color="#d97706"></v-progress-linear>
          </div>

          <div class="mb-4">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span>Non-Venomous (Rat Snake / Python / Keelback)</span>
              <span class="teal--text text--darken-3 font-weight-black">{{ predictiveStats.nonVenomousCount }}</span>
            </div>
            <v-progress-linear :value="predictiveStats.total > 0 ? (predictiveStats.nonVenomousCount / predictiveStats.total) * 100 : 0" height="8" rounded color="#059669"></v-progress-linear>
          </div>

          <!-- AI Field Action Recommendation -->
          <div class="ai-field-recommendation pa-3 rounded-lg">
            <div class="d-flex align-center mb-1">
              <v-icon color="#0f766e" small class="mr-1">mdi-auto-fix</v-icon>
              <span class="font-weight-black text-uppercase caption teal--text text--darken-4">Predictive RRT Recommendation</span>
            </div>
            <p class="caption text--secondary mb-0">
              {{ predictiveStats.recommendation }}
            </p>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- SPATIOTEMPORAL ENCOUNTER FORECAST CARD -->
      <v-card v-if="activePanel === 'spatial'" key="card-spatial" class="floating-overlay-card predictive-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-3">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3 d-flex align-center">
              <v-icon color="#0f766e" class="mr-1">mdi-crystal-ball</v-icon>
              Spatiotemporal Forecast
            </h3>
            <p class="panel-card-subtitle ma-0">Predicted encounter hotspots by month, hour & species.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <div class="mb-3">
            <span class="field-label mb-1 d-block font-weight-bold">Month</span>
            <v-select
              v-model="forecastMonth"
              :items="monthOptions"
              item-text="text"
              item-value="value"
              outlined dense hide-details color="#0f766e" item-color="#0f766e"
              :menu-props="{ offsetY: true, zIndex: 100000, contentClass: 'sarpa-panel-menu' }"
            ></v-select>
          </div>

          <div class="mb-3">
            <span class="field-label mb-1 d-block font-weight-bold">Species (optional)</span>
            <v-autocomplete
              v-model="forecastSpecies"
              :items="snakeitems"
              item-text="name"
              item-value="_id"
              placeholder="All species"
              clearable outlined dense hide-details color="#0f766e"
              :menu-props="{ offsetY: true, zIndex: 100000, contentClass: 'sarpa-panel-menu' }"
            ></v-autocomplete>
          </div>

          <div class="mb-3">
            <span class="field-label mb-1 d-block font-weight-bold">Time of day (optional)</span>
            <v-select
              v-model="forecastHour"
              :items="hourOptions"
              item-text="text"
              item-value="value"
              outlined dense hide-details color="#0f766e" item-color="#0f766e"
              :menu-props="{ offsetY: true, zIndex: 100000, contentClass: 'sarpa-panel-menu' }"
            ></v-select>
          </div>

          <div class="d-flex gap-2 mb-3">
            <v-btn class="flex-grow-1 white--text" color="#0f766e" depressed :loading="spatialForecastLoading" @click="fetchSpatialForecast">
              <v-icon left small>mdi-map-search-outline</v-icon> Generate Forecast
            </v-btn>
            <v-btn outlined color="#0f766e" depressed @click="clearSpatialForecast">Clear</v-btn>
          </div>

          <div v-if="spatialForecastData">
            <div class="pa-3 rounded-lg mb-3" style="background: #f0fdf4; border: 1px solid #bbf7d0;">
              <div class="caption font-weight-black teal--text text--darken-4 mb-1">
                {{ spatialForecastData.cells.length }} predicted hotspot cells · {{ spatialForecastData.monthLabel }}
                <span v-if="spatialForecastData.hour !== null">· {{ formatHour(spatialForecastData.hour) }}</span>
              </div>
              <div class="caption grey--text text--darken-2" style="font-size: 11px;">
                Based on {{ spatialForecastData.years }} year(s) of history · {{ spatialForecastData.totalEncounters.toLocaleString() }} matching encounters.
                Colour = relative encounter likelihood.
              </div>
            </div>

            <!-- Likelihood legend -->
            <div class="d-flex align-center mb-3" style="gap: 4px;">
              <span class="caption grey--text" style="font-size: 10px;">Low</span>
              <div style="flex: 1; height: 10px; border-radius: 5px; background: linear-gradient(to right, #2563eb, #22c55e, #f59e0b, #dc2626);"></div>
              <span class="caption grey--text" style="font-size: 10px;">High</span>
            </div>

            <!-- Top predicted cells -->
            <div class="caption font-weight-black grey--text text--darken-3 mb-1">Top Predicted Zones</div>
            <div
              v-for="(cell, i) in spatialForecastData.cells.slice(0, 8)"
              :key="'cell-' + i"
              class="pa-2 rounded mb-2 d-flex align-center justify-space-between"
              style="background: #f8fafc; border: 1px solid #e2e8f0; cursor: pointer;"
              @click="flyToCell(cell)"
            >
              <div class="min-width-0">
                <div class="font-weight-bold text-truncate" style="font-size: 11px; color: #0f766e;">
                  {{ cell.topSpecies }}
                </div>
                <div class="caption grey--text text--darken-2" style="font-size: 10px;">
                  {{ cell.lat.toFixed(3) }}, {{ cell.lon.toFixed(3) }} · ~{{ cell.perYear }}/yr
                </div>
              </div>
              <v-chip x-small :color="intensityColor(cell.intensity)" class="white--text font-weight-black">
                {{ Math.round(cell.intensity * 100) }}%
              </v-chip>
            </div>
          </div>
          <div v-else class="caption text-center grey--text text--darken-1 py-4">
            Choose a month (and optionally species / time of day), then generate the forecast to plot predicted encounter hotspots on the map.
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <v-card v-if="activePanel === 'ops'" key="card-ops" class="floating-overlay-card predictive-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-3">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3 d-flex align-center">
              <v-icon color="#0f766e" class="mr-1">mdi-brain</v-icon>
              AI Ops Center
            </h3>
            <p class="panel-card-subtitle ma-0">Alerts, hotspot outlook, escalation logic, simulator and map intelligence.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <div class="caption font-weight-black grey--text text--darken-3 mb-2">Live AI Alerts</div>
          <div class="d-flex flex-wrap mb-3" style="gap: 6px;">
            <v-chip
              v-for="alert in aiOpsAlerts.slice(0, 6)"
              :key="alert.id"
              small
              :color="alert.color"
              class="white--text font-weight-black"
              @click="focusAiAlert(alert)"
            >
              {{ alert.label }}
            </v-chip>
          </div>

          <div class="d-flex flex-wrap mb-4" style="gap: 10px;">
            <v-checkbox
              v-model="showBacklogOverlay"
              label="Backlog Risk Overlay"
              color="#f97316"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleBacklogRiskOverlay"
            ></v-checkbox>
            <v-checkbox
              v-model="showAnomalyOverlay"
              label="Anomaly Overlay"
              color="#7c3aed"
              hide-details
              dense
              class="ma-0 pa-0"
              @change="toggleAnomalyRiskOverlay"
            ></v-checkbox>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#eff6ff; border:1px solid #bfdbfe;">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="caption font-weight-black text-uppercase blue--text text--darken-3">
                Temporal Hotspot Outlook
              </div>
              <v-chip x-small color="blue lighten-5" class="blue--text text--darken-3 font-weight-black">
                {{ temporalOutlookFrames.length }} frame{{ temporalOutlookFrames.length === 1 ? '' : 's' }}
              </v-chip>
            </div>

            <v-chip-group v-model="temporalOutlookHorizon" mandatory active-class="blue darken-2 white--text" class="ma-0 mb-2">
              <v-chip filter x-small :value="6">Next 6h</v-chip>
              <v-chip filter x-small :value="12">Next 12h</v-chip>
              <v-chip filter x-small :value="24">Next 24h</v-chip>
            </v-chip-group>

            <div class="d-flex mb-3" style="gap: 8px;">
              <v-btn small depressed color="#2563eb" class="white--text" :loading="temporalOutlookLoading" @click="generateTemporalOutlook">
                <v-icon left x-small>mdi-timeline-clock-outline</v-icon>
                Generate Outlook
              </v-btn>
              <v-btn small outlined color="#2563eb" :disabled="temporalOutlookFrames.length < 2" @click="toggleTemporalPlayback">
                <v-icon left x-small>{{ temporalOutlookPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                {{ temporalOutlookPlaying ? 'Pause' : 'Play' }}
              </v-btn>
              <v-btn small text color="#2563eb" :disabled="temporalOutlookFrames.length === 0" @click="clearTemporalOutlook">
                Clear
              </v-btn>
            </div>

            <div v-if="activeTemporalFrame" class="pa-2 rounded" style="background:#ffffff; border:1px solid #dbeafe;">
              <div class="font-weight-black" style="font-size:11px; color:#1d4ed8;">
                {{ activeTemporalFrame.label }}
              </div>
              <div class="caption grey--text text--darken-2">
                {{ activeTemporalFrame.summary }}
              </div>
              <div class="d-flex flex-wrap mt-2" style="gap: 6px;">
                <v-chip x-small color="red lighten-5" class="red--text text--darken-3 font-weight-black">
                  Top species: {{ activeTemporalFrame.topSpecies }}
                </v-chip>
                <v-chip x-small color="amber lighten-5" class="amber--text text--darken-4 font-weight-black">
                  Hotspots: {{ activeTemporalFrame.hotspotCount }}
                </v-chip>
                <v-chip x-small color="green lighten-5" class="green--text text--darken-4 font-weight-black">
                  Confidence: {{ activeTemporalFrame.confidence }}%
                </v-chip>
              </div>
            </div>

            <div v-if="temporalOutlookFrames.length > 1" class="mt-3">
              <div class="caption font-weight-bold grey--text text--darken-2 mb-1">Outlook Frames</div>
              <div
                v-for="(frame, frameIndex) in temporalOutlookFrames"
                :key="frame.id"
                class="pa-2 rounded mb-2 d-flex align-center justify-space-between"
                :style="{
                  background: frameIndex === temporalOutlookIndex ? '#dbeafe' : '#f8fafc',
                  border: frameIndex === temporalOutlookIndex ? '1px solid #60a5fa' : '1px solid #e2e8f0',
                  cursor: 'pointer'
                }"
                @click="selectTemporalOutlookFrame(frameIndex)"
              >
                <div class="min-width-0">
                  <div class="font-weight-bold text-truncate" style="font-size:11px; color:#0f172a;">{{ frame.label }}</div>
                  <div class="caption grey--text text--darken-2" style="font-size:10px;">{{ frame.topSpecies }} · {{ frame.hotspotCount }} hotspots · {{ frame.confidence }}% confidence</div>
                </div>
                <v-chip x-small :color="frameIndex === temporalOutlookIndex ? 'blue darken-2' : 'blue lighten-5'" :class="frameIndex === temporalOutlookIndex ? 'white--text' : 'blue--text text--darken-2'" class="font-weight-black">
                  {{ frame.topIntensity }}%
                </v-chip>
              </div>
            </div>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#f8fafc; border:1px solid #cbd5e1;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-2">Map Click Intelligence</div>
            <div v-if="clickedMapInsight">
              <div class="font-weight-black mb-1" style="font-size:12px; color:#0f766e;">
                {{ clickedMapInsight.district }} · {{ clickedMapInsight.coordsLabel }}
              </div>
              <div class="caption grey--text text--darken-2 mb-2">
                {{ clickedMapInsight.narrative }}
              </div>
              <div class="d-flex flex-wrap mb-2" style="gap: 6px;">
                <v-chip x-small color="teal lighten-5" class="teal--text text--darken-4 font-weight-black">Likely species: {{ clickedMapInsight.topSpecies }}</v-chip>
                <v-chip x-small color="red lighten-5" class="red--text text--darken-3 font-weight-black">Threat: {{ clickedMapInsight.threatLevel }}</v-chip>
                <v-chip x-small color="amber lighten-5" class="amber--text text--darken-4 font-weight-black">ETA: {{ clickedMapInsight.etaMinutes }} mins</v-chip>
                <v-chip x-small color="blue lighten-5" class="blue--text text--darken-3 font-weight-black">Confidence: {{ clickedMapInsight.confidence }}%</v-chip>
              </div>
              <div class="caption" style="color:#334155;">
                <strong>Escalation:</strong> {{ clickedMapInsight.escalation }}.
                <strong>Deployment:</strong> {{ clickedMapInsight.deploymentRecommendation }}.
              </div>
            </div>
            <div v-else class="caption grey--text text--darken-1">
              Click anywhere on the map to get inferred species likelihood, response ETA, threat level and escalation advice for that location.
            </div>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#fff7ed; border:1px solid #fed7aa;">
            <div class="caption font-weight-black orange--text text--darken-4 mb-2">Backlog Breach Watchlist</div>
            <div
              v-for="district in backlogRiskDistricts.slice(0, 4)"
              :key="'backlog-' + district.district"
              class="pa-2 rounded mb-2"
              style="background:#ffffff; border:1px solid #ffedd5;"
            >
              <div class="d-flex justify-space-between align-center">
                <div class="font-weight-black" style="font-size:11px; color:#9a3412;">{{ district.district }}</div>
                <v-chip x-small :color="district.breachRisk >= 75 ? 'deep-orange darken-2' : 'amber darken-3'" class="white--text font-weight-black">
                  {{ district.breachRisk }}% breach risk
                </v-chip>
              </div>
              <div class="caption grey--text text--darken-2" style="font-size:10px;">
                {{ district.backlog }} backlog · {{ district.backlogRate }}% rate · {{ district.avgArrivalMinsLabel }} arrival
              </div>
              <div class="caption" style="color:#7c2d12;">{{ district.recommendation }}</div>
            </div>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#faf5ff; border:1px solid #e9d5ff;">
            <div class="caption font-weight-black purple--text text--darken-3 mb-2">Anomaly & Escalation Queue</div>
            <div
              v-for="item in anomalyDistricts.slice(0, 4)"
              :key="'anomaly-' + item.district"
              class="pa-2 rounded mb-2"
              style="background:#ffffff; border:1px solid #ede9fe;"
            >
              <div class="d-flex justify-space-between align-center">
                <div class="font-weight-black" style="font-size:11px; color:#6b21a8;">{{ item.district }}</div>
                <v-chip x-small :color="item.severityColor" class="white--text font-weight-black">{{ item.severityLabel }}</v-chip>
              </div>
              <div class="caption grey--text text--darken-2" style="font-size:10px;">{{ item.reason }}</div>
              <div class="caption" style="color:#4c1d95;"><strong>Escalate:</strong> {{ item.escalation }}</div>
            </div>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#ecfdf5; border:1px solid #bbf7d0;">
            <div class="caption font-weight-black green--text text--darken-4 mb-2">AI Deployment Recommendations</div>
            <div
              v-for="rec in deploymentRecommendations.slice(0, 4)"
              :key="rec.id"
              class="pa-2 rounded mb-2"
              style="background:#ffffff; border:1px solid #d1fae5;"
            >
              <div class="d-flex justify-space-between align-center">
                <div class="font-weight-black" style="font-size:11px; color:#065f46;">{{ rec.title }}</div>
                <v-chip x-small :color="rec.color" class="white--text font-weight-black">{{ rec.priority }}</v-chip>
              </div>
              <div class="caption grey--text text--darken-2" style="font-size:10px;">{{ rec.subtitle }}</div>
              <div class="caption" style="color:#166534;">{{ rec.action }}</div>
            </div>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#f0fdf4; border:1px solid #bbf7d0;">
            <div class="caption font-weight-black green--text text--darken-4 mb-2">What-If Rescuer Simulator</div>
            <v-select
              v-model="selectedSimulationDistrict"
              :items="simulationDistrictOptions"
              label="District"
              outlined
              dense
              hide-details
              color="#0f766e"
              class="mb-3"
              :menu-props="{ offsetY: true, zIndex: 100000, contentClass: 'sarpa-panel-menu' }"
            ></v-select>

            <div class="caption font-weight-bold grey--text text--darken-2 mb-1">Additional rescuers</div>
            <v-slider
              v-model="simulationExtraRescuers"
              :min="0"
              :max="8"
              step="1"
              thumb-label="always"
              color="#0f766e"
              track-color="#bbf7d0"
              class="mt-0 pt-0"
            ></v-slider>

            <div v-if="whatIfProjection" class="pa-2 rounded" style="background:#ffffff; border:1px solid #d1fae5;">
              <div class="caption grey--text text--darken-2 mb-1">{{ whatIfProjection.summary }}</div>
              <div class="d-flex flex-wrap" style="gap: 6px;">
                <v-chip x-small color="teal lighten-5" class="teal--text text--darken-4 font-weight-black">ETA {{ whatIfProjection.projectedEta }} mins</v-chip>
                <v-chip x-small color="amber lighten-5" class="amber--text text--darken-4 font-weight-black">Backlog {{ whatIfProjection.projectedBacklog }}</v-chip>
                <v-chip x-small color="blue lighten-5" class="blue--text text--darken-3 font-weight-black">Load {{ whatIfProjection.projectedLoadIndex }}x</v-chip>
                <v-chip x-small color="green lighten-5" class="green--text text--darken-4 font-weight-black">Need left {{ whatIfProjection.remainingNeed }}</v-chip>
              </div>
            </div>
          </div>

          <div class="mb-4 pa-3 rounded-lg" style="background:#f8fafc; border:1px solid #cbd5e1;">
            <div class="caption font-weight-black grey--text text--darken-3 mb-2">Selection Narrative & Pattern Clusters</div>
            <div class="caption mb-3" style="color:#334155;">{{ selectionNarrative }}</div>
            <div
              v-for="cluster in operationalPatternClusters"
              :key="cluster.label"
              class="pa-2 rounded mb-2"
              style="background:#ffffff; border:1px solid #e2e8f0;"
            >
              <div class="font-weight-black" style="font-size:11px; color:#1e293b;">{{ cluster.label }}</div>
              <div class="caption grey--text text--darken-2">{{ cluster.detail }}</div>
            </div>
          </div>

          <div class="pa-3 rounded-lg" style="background:#eff6ff; border:1px solid #bfdbfe;">
            <div class="caption font-weight-black blue--text text--darken-3 mb-2">Repeat Conflict Watchlist</div>
            <div
              v-for="item in repeatConflictPredictions.slice(0, 4)"
              :key="'repeat-' + item.district"
              class="pa-2 rounded mb-2"
              style="background:#ffffff; border:1px solid #dbeafe;"
            >
              <div class="d-flex justify-space-between align-center">
                <div class="font-weight-black" style="font-size:11px; color:#1d4ed8;">{{ item.district }}</div>
                <v-chip x-small color="blue darken-2" class="white--text font-weight-black">{{ item.repeatRisk }}% repeat risk</v-chip>
              </div>
              <div class="caption grey--text text--darken-2">{{ item.reason }}</div>
            </div>
          </div>
        </div>
      </v-card>
    </transition>

    <transition name="slide-fade">
      <!-- Spatial Stats Overlay Card -->
      <v-card v-if="activePanel === 'stats'" key="card-stats" class="floating-overlay-card stats-card-overlay" elevation="8">
        <div class="panel-card-header d-flex justify-space-between align-center mb-4">
          <div>
            <h3 class="panel-card-title teal--text text--darken-3">Spatial Selection</h3>
            <p class="panel-card-subtitle ma-0">Analytics for drawn bounds vs global scope.</p>
          </div>
          <v-btn icon small @click="activePanel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="panel-card-content stats-scroll-container">
          <!-- Selection status bar -->
          <div class="selection-status-bar mb-4" :class="{ 'selection-status-bar--active': isSelectionActive }">
            <div class="d-flex align-center justify-space-between width-100">
              <div>
                <div class="status-title">{{ isSelectionActive ? 'POLYGON ACTIVE' : 'NO DRAWN BOUNDS' }}</div>
                <div class="status-desc">
                  {{ isSelectionActive ? `${selectedRecords.length} records selected` : 'Displaying global stats' }}
                </div>
              </div>
              <v-btn
                v-if="isSelectionActive"
                x-small
                color="red"
                outlined
                @click="clearSelection"
              >
                Clear Shape
              </v-btn>
            </div>
          </div>

          <!-- Species List counts inside selection -->
          <div class="species-stats-wrap flex-grow-1">
            <div class="stats-label mb-2 font-weight-black grey--text text--darken-3">Species Density Breakdown</div>
            <div class="stats-list" v-if="selectionStats.length > 0">
              <div
                v-for="(item, index) in selectionStats.slice(0, 10)"
                :key="'stat-' + item.name + '-' + index"
                class="species-stat-row d-flex align-center mb-2"
              >
                <span class="species-label text-truncate" :title="item.name" style="width: 110px; font-size:11px;">{{ item.name }}</span>
                <div class="species-progress-wrap flex-grow-1 mx-2">
                  <v-progress-linear
                    :value="getSpeciesPercentage(item.count)"
                    height="8"
                    rounded
                    color="#0f766e"
                    background-color="#edf2eb"
                  ></v-progress-linear>
                </div>
                <span class="species-value font-weight-bold" style="font-size:11px;">{{ item.count }}</span>
              </div>
            </div>
            <div v-else class="empty-state text-center py-8">
              <v-icon large color="#cbd5e1">mdi-map-marker-off</v-icon>
              <div class="empty-text mt-2">No records in selected area</div>
            </div>
          </div>

          <!-- Selection details list -->
          <div class="selection-logs-wrap mt-4 pt-4 border-top" v-if="isSelectionActive && selectedRecords.length > 0">
            <div class="stats-label mb-2 font-weight-black grey--text text--darken-3">Selected Incidents (Top 5)</div>
            <div class="selected-logs-list">
              <div
                v-for="item in selectedRecords.slice(0, 5)"
                :key="'log-' + item._id"
                class="log-tile mb-2 pa-2 d-flex align-start gap-2 rounded-lg"
                style="background: #f8fafc; border: 1px solid #e2e8f0;"
              >
                <div class="log-tile__img-wrap" v-if="item.snake && item.snake.photographs && item.snake.photographs.length > 0">
                  <img :src="mediaURL + item.snake.photographs[0].url" class="log-tile__img" />
                </div>
                <div class="log-tile__img-wrap" v-else>
                  <div class="log-tile__placeholder"><v-icon small color="#94a3b8">mdi-image-off</v-icon></div>
                </div>
                <div class="log-tile__details min-width-0">
                  <div class="log-tile__title text-truncate font-weight-bold" style="font-size:12px; color:#1e293b;">{{ (item.snake && item.snake.name) || 'Unknown' }}</div>
                  <div class="log-tile__desc text-truncate grey--text text--darken-2" style="font-size:10px;">Locality: {{ item.locationname || 'N/A' }}</div>
                  <div class="log-tile__date grey--text text--darken-2" style="font-size:9px;">Date: {{ formatDate(item.createdAt) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </transition>

    <!-- FLOATING PREDICTIVE INSPECTOR DRAWER FOR SELECTED POINTER -->
    <transition name="slide-up">
      <div v-if="selectedPointer" class="predictive-pointer-inspector">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center gap-2">
            <v-chip x-small color="#0f766e" class="white--text font-weight-black">
              {{ selectedPointer.isVenomous ? 'VENOMOUS SPECIES' : 'NON-VENOMOUS' }}
            </v-chip>
            <span class="caption text-uppercase font-weight-bold grey--text text--darken-2">
              AI Risk Index: {{ selectedPointer.riskScore }}%
            </span>
          </div>
          <v-btn icon x-small @click="selectedPointer = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="d-flex align-center gap-3">
          <div class="inspector-img-wrap" v-if="selectedPointer.snake && selectedPointer.snake.photographs && selectedPointer.snake.photographs.length > 0">
            <img :src="mediaURL + selectedPointer.snake.photographs[0].url" class="inspector-img" />
          </div>
          <div class="inspector-img-wrap" v-else>
            <div class="inspector-img-placeholder"><v-icon color="#94a3b8">mdi-snake</v-icon></div>
          </div>

          <div class="inspector-info flex-grow-1">
            <h4 class="inspector-name text-truncate teal--text text--darken-4 ma-0">
              {{ (selectedPointer.snake && selectedPointer.snake.name) || 'Unknown Snake' }}
            </h4>
            <div class="caption font-weight-bold text--primary mt-1">
              Location: {{ selectedPointer.locationname || 'Not specified' }}
            </div>
            <div class="caption text--secondary">
              Reported By: {{ (selectedPointer.user && selectedPointer.user.name) || 'Public User' }}
            </div>
            <div class="caption grey--text text--darken-2">
              Date: {{ formatDate(selectedPointer.createdAt) }}
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Floating bottom timeline summary overlay -->
    <div class="floating-timeline-card d-flex align-center gap-3">
      <div class="timeline-icon-wrap">
        <v-icon color="#0f766e">mdi-calendar-clock</v-icon>
      </div>
      <div>
        <div class="timeline-title">Active Time Frame</div>
        <div class="timeline-dates" v-if="timeperiod">
          {{ formatDate(fromdate) }} to {{ formatDate(todate) }}
        </div>
        <div class="timeline-dates" v-else>
          All-time records list
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import html2canvas from "html2canvas";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet.heat";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

delete L.Icon.Default.prototype._getIconUrl;
const retinaPath = require("leaflet/dist/images/marker-icon-2x.png");
const defaultIconPath = require("leaflet/dist/images/marker-icon.png");
const shadowPath = require("leaflet/dist/images/marker-shadow.png");

L.Icon.Default.mergeOptions({
  iconRetinaUrl: retinaPath && retinaPath.default ? retinaPath.default : retinaPath,
  iconUrl: defaultIconPath && defaultIconPath.default ? defaultIconPath.default : defaultIconPath,
  shadowUrl: shadowPath && shadowPath.default ? shadowPath.default : shadowPath,
});

// Used by the map renderer to colour venomous vs non-venomous pointers.
// (Predictive venom classification now happens server-side in /location/predictive.)
const HIGHLY_VENOMOUS_SPECIES = new Set([
  "spectacled cobra", "cobra", "common krait", "krait", "russell's viper",
  "russells viper", "viper", "saw scaled viper", "king cobra", "hump-nosed pit viper",
  "banded krait"
]);

const DISTRICT_COORDS = {
  kannur: [11.8745, 75.3704],
  ernakulam: [9.9816, 76.2999],
  kozhikode: [11.2588, 75.7804],
  thiruvananthapuram: [8.5241, 76.9366],
  kottayam: [9.5916, 76.5222],
  malappuram: [11.072, 76.074],
  thrissur: [10.5276, 76.2144],
  palakkad: [10.7867, 76.6548],
  wayanad: [11.6854, 76.132],
  idukki: [9.8494, 76.9806],
  pathanamthitta: [9.2648, 76.787],
  alappuzha: [9.4981, 76.3388],
  kollam: [8.8932, 76.6141],
  kasaragod: [12.5102, 74.9852],
};

const DISTRICT_ALIASES = {
  trivandrum: "thiruvananthapuram",
  cochin: "ernakulam",
  kochi: "ernakulam",
  calicut: "kozhikode",
  cannanore: "kannur",
};

export default {
  data() {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    return {
      mediaURL: process.env.VUE_APP_MEDIA_URL || "",
      appLoading: true,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      data: [],
      totalcount: 0,
      snakeitems: [],
      userslist: [],
      timeperiod: false,
      bigsnakes: false,
      allsnakes: false,
      users: false,
      searchUser: null,
      fromdate: sixMonthsAgo.toISOString().substring(0, 10),
      menu: false,
      divisioncheck: false,
      districtcheck: false,
      district: "",
      districtList: [
        "All", "Palakkad", "Alappuzha", "Malappuram", "Kannur", "Wayanad",
        "Kozhikode", "Thrissur", "Thiruvananthapuram", "Kottayam", "Idukki",
        "Ernakulam", "Kollam", "Pathanamthitta", "Kasaragod"
      ],
      todate: today.toISOString().substring(0, 10),
      menu2: false,
      bigsnakeselect: [],
      bigsnakestypes: [
        { text: "Spectacled Cobra", value: "5c98a6f9db9c5d619e5a721c" },
        { text: "Common Krait", value: "5c98a7f9db9c5d619e5a721d" },
        { text: "Russell's Viper", value: "5c988da5db9c5d619e5a7218" },
        { text: "Saw Scaled Viper", value: "5c988c39db9c5d619e5a7217" }
      ],
      allsnakelist: [],
      selecteduser: "",
      alldata: true,
      divisionname: "",
      divisionList: [],
      rangeList: [],
      rangename: "",

      // Map overlays selection states
      showPointers: false,
      showHotspots: true,
      showHeatmap: false,
      isFullscreen: false,
      isSelectionActive: false,
      selectedRecords: [],
      selectionStats: [],
      selectedPointer: null,

      // GIS Spatial Boundary Layers
      gisLayers: {
        districts: false,
        divisions: false,
        ranges: false,
        pas: false,
      },
      geoJsonData: {
        districts: null,
        divisions: null,
        ranges: null,
        pas: null,
      },
      geoJsonLeafletLayers: {
        districts: null,
        divisions: null,
        ranges: null,
        pas: null,
      },

      showShortageMapOverlay: false,
      selectedProbabilitySpecies: "",
      shortageLayerGroup: null,
      aiQueryInputText: "",
      aiQueryResult: null,

      selectedSeason: "all",
      seasonOptions: [
        { text: "All Year", value: "all" },
        { text: "Current Month", value: "current" },
        { text: "Monsoon (Jun-Sep)", value: "monsoon" },
        { text: "Post-Monsoon (Oct-Dec)", value: "post_monsoon" },
        { text: "Summer (Jan-May)", value: "summer" },
      ],
      activePanel: null,
      showDrawControls: true,

      loadingMessages: [
        "Fetching 80,000+ historical SARPA rescue logs...",
        "Parsing spatial coordinates across 14 Kerala districts...",
        "Aggregating species threat levels & venomous encounter ratios...",
        "Indexing GIS forest divisions, ranges & protected areas...",
        "Optimizing high-density Leaflet canvas rendering engine...",
        "Finalizing interactive spatial map layers..."
      ],
      loadingMsgIndex: 0,
      loadingTimer: null,
      hasFetchedRecords: false,
      exportLoading: false,

      // Real predictive analytics payload from POST /location/predictive
      predictiveData: null,
      predictiveLoading: false,

      // Spatiotemporal encounter forecast (POST /location/spatial-forecast)
      forecastMonth: new Date().getMonth() + 1,
      forecastHour: null,
      forecastSpecies: null,
      spatialForecastData: null,
      spatialForecastLoading: false,
      monthOptions: [
        { text: "January", value: 1 }, { text: "February", value: 2 }, { text: "March", value: 3 },
        { text: "April", value: 4 }, { text: "May", value: 5 }, { text: "June", value: 6 },
        { text: "July", value: 7 }, { text: "August", value: 8 }, { text: "September", value: 9 },
        { text: "October", value: 10 }, { text: "November", value: 11 }, { text: "December", value: 12 },
      ],

      showBacklogOverlay: false,
      showAnomalyOverlay: false,
      backlogRiskLayerGroup: null,
      anomalyRiskLayerGroup: null,
      temporalOutlookLayerGroup: null,
      temporalOutlookLoading: false,
      temporalOutlookHorizon: 12,
      temporalOutlookFrames: [],
      temporalOutlookIndex: 0,
      temporalOutlookPlaying: false,
      temporalOutlookTimer: null,
      clickedMapInsight: null,
      clickedMapLatLng: null,
      selectedSimulationDistrict: "",
      simulationExtraRescuers: 2,
    };
  },
  computed: {
    currentLoadingMessage() {
      return this.loadingMessages[this.loadingMsgIndex] || "Fetching data, please wait...";
    },

    canExportExcel() {
      const routeRole = this.$route && this.$route.meta ? this.$route.meta.role : "";
      const storeState = this.$store ? this.$store.state : {};
      return !(
        routeRole === "analyst" ||
        storeState.mainRole === "analyst" ||
        storeState.userRole === "analyst" ||
        String(storeState.loginType || "").toUpperCase() === "ANALYST"
      );
    },

    // Species threat classification & venom ratio — all measured server-side.
    predictiveStats() {
      const d = this.predictiveData;
      if (!d) {
        return {
          total: 0, highlyVenomousCount: 0, mildlyVenomousCount: 0, nonVenomousCount: 0,
          venomRatio: 0, recommendation: "Loading predictive analytics...",
        };
      }
      const t = d.totals;
      const venomRatio = t.venomRatio || 0;
      let recommendation = "Standard field monitoring recommended. Low venomous encounter concentration.";
      if (venomRatio > 35) {
        recommendation = "High concentration of highly venomous species detected (>35%). Deploy trained RRT handlers with anti-venom protocols.";
      } else if (venomRatio > 15) {
        recommendation = "Moderate venomous snake presence. Rapid response teams advised to maintain standard safety equipment.";
      }
      return {
        total: t.operations,
        highlyVenomousCount: t.highlyVenomous,
        mildlyVenomousCount: t.mildlyVenomous,
        nonVenomousCount: t.nonVenomous,
        venomRatio,
        recommendation,
      };
    },

    // Rescuer shortage forecast + seasonal surge — mapped from the real endpoint shape.
    deepPredictiveStats() {
      const d = this.predictiveData;
      if (!d) {
        return {
          shortageDivisions: [], shortageCount: 0, topSurgeSpecies: "None",
          surgeInsight: "Loading predictive analytics...", seasonalEncountersCount: 0,
        };
      }
      const shortageDivisions = (d.shortage || []).map((s) => ({
        name: s.district,
        count: s.total,
        activeRescuers: s.activeRescuers,
        incidentsPerRescuer: s.incidentsPerRescuer != null ? s.incidentsPerRescuer : "—",
        loadIndex: s.loadIndex != null ? s.loadIndex : null,
        needed: s.rescuersNeeded,
        riskLevel: s.riskLevel,
      }));
      const topSp = (d.species && d.species.length) ? d.species[0] : null;
      const topSurgeSpecies = topSp ? topSp.name : "None";
      const seasonName = (d.window && d.window.seasonLabel) || "the current window";
      const surgeInsight = topSp
        ? `During ${seasonName}, ${topSp.name} accounts for ${topSp.share}% of identified rescues. ${d.forecast ? d.forecast.insight : ""}`
        : "No species encounter data available for this window.";
      return {
        shortageDivisions,
        shortageCount: shortageDivisions.length,
        topSurgeSpecies,
        surgeInsight,
        seasonalEncountersCount: d.totals.operations,
      };
    },

    availableSpeciesList() {
      // Merge species seen in the current window with every known species, so the
      // dropdown is always fully populated regardless of endpoint state.
      const names = new Set();
      const d = this.predictiveData;
      if (d && d.species) {
        d.species.forEach((s) => { if (s && s.name) names.add(s.name.trim()); });
      }
      (this.snakeitems || []).forEach((s) => {
        if (s && s.name && s.name.trim().toLowerCase() !== "unknown") names.add(s.name.trim());
      });
      return Array.from(names).filter(Boolean).sort();
    },

    // Per-district spatial probability for the selected species (or each district's top species).
    speciesProbabilityBreakdown() {
      const d = this.predictiveData;
      if (!d || !d.districts) return [];
      const sel = (this.selectedProbabilitySpecies || "").trim().toLowerCase();

      if (sel) {
        // Show ALL districts ranked by this species' share (including 0%) so the
        // selection always produces a visible, informative result.
        return d.districts
          .map((dist) => {
            const chances = dist.speciesChances || [];
            const match = chances.find(
              (sp) => sp.name.toLowerCase().includes(sel) || sel.includes(sp.name.toLowerCase())
            );
            return {
              district: dist.district,
              speciesName: this.selectedProbabilitySpecies,
              chance: match ? match.chance : 0,
            };
          })
          .sort((a, b) => b.chance - a.chance)
          .slice(0, 14);
      }

      // Nothing selected: each district's dominant species.
      return d.districts
        .map((dist) => {
          const top = (dist.speciesChances || [])[0];
          return {
            district: dist.district,
            speciesName: top ? top.name : "Primary Species",
            chance: top ? top.chance : 0,
          };
        })
        .sort((a, b) => b.chance - a.chance)
        .slice(0, 7);
    },

    // Bars for the forecast mini-chart: last 12 months of history + projected months.
    forecastBars() {
      const d = this.predictiveData;
      if (!d || !d.forecast) return [];
      const hist = (d.forecast.history || []).slice(-12);
      const proj = d.forecast.projection || [];
      const all = [...hist, ...proj];
      if (!all.length) return [];
      const max = Math.max(1, ...all.map((x) => x.count));
      return all.map((x) => ({
        label: x.label,
        count: x.count,
        projected: !!x.projected,
        displayPct: Math.max(4, Math.round((x.count / max) * 100)),
      }));
    },

    hourOptions() {
      const opts = [{ text: "Any time", value: null }];
      for (let h = 0; h < 24; h++) {
        opts.push({ text: this.formatHour(h), value: h });
      }
      return opts;
    },

    simulationDistrictOptions() {
      if (this.predictiveData && this.predictiveData.districts && this.predictiveData.districts.length) {
        return this.predictiveData.districts.map((d) => d.district);
      }
      return this.districtList.filter((item) => item && item !== "All");
    },

    activeTemporalFrame() {
      if (!this.temporalOutlookFrames.length) return null;
      return this.temporalOutlookFrames[this.temporalOutlookIndex] || this.temporalOutlookFrames[0];
    },

    backlogRiskDistricts() {
      const districts = (this.predictiveData && this.predictiveData.districts) || [];
      return districts
        .map((district) => {
          const arrival = district.avgArrivalMins != null ? district.avgArrivalMins : 25;
          const breachRisk = Math.min(
            99,
            Math.round(
              (district.backlogRate || 0) * 1.8 +
              Math.max(0, arrival - 20) * 1.5 +
              Math.max(0, (district.loadIndex || 1) - 1) * 18
            )
          );
          return {
            ...district,
            breachRisk,
            avgArrivalMinsLabel: district.avgArrivalMins != null ? `${district.avgArrivalMins} mins` : "No SLA data",
            recommendation: breachRisk >= 75
              ? `Prioritize assignment immediately and shift extra support into ${district.district}.`
              : `Monitor queue buildup and keep an on-call rescuer buffer ready in ${district.district}.`,
          };
        })
        .filter((district) => district.backlog > 0 || district.breachRisk >= 40)
        .sort((a, b) => b.breachRisk - a.breachRisk);
    },

    anomalyDistricts() {
      const districts = (this.predictiveData && this.predictiveData.districts) || [];
      if (!districts.length) return [];

      const avgTotal = districts.reduce((sum, district) => sum + district.total, 0) / districts.length;
      const avgBacklog = districts.reduce((sum, district) => sum + district.backlogRate, 0) / districts.length;
      const avgVenom = districts.reduce((sum, district) => sum + district.venomPercent, 0) / districts.length;

      return districts
        .map((district) => {
          const totalFactor = avgTotal > 0 ? district.total / avgTotal : 0;
          const backlogFactor = avgBacklog > 0 ? district.backlogRate / avgBacklog : 0;
          const venomFactor = avgVenom > 0 ? district.venomPercent / avgVenom : 0;
          const anomalyScore = Number((totalFactor * 0.45 + backlogFactor * 0.35 + venomFactor * 0.2).toFixed(2));

          let severityLabel = "Watch";
          let severityColor = "deep-purple lighten-1";
          if (anomalyScore >= 2 || district.riskLevel === "CRITICAL DEFICIT") {
            severityLabel = "Escalate";
            severityColor = "deep-purple darken-3";
          } else if (anomalyScore >= 1.5 || district.riskLevel === "HIGH DEFICIT") {
            severityLabel = "High";
            severityColor = "purple darken-2";
          }

          return {
            district: district.district,
            anomalyScore,
            severityLabel,
            severityColor,
            reason: `${district.total.toLocaleString()} incidents, ${district.backlogRate}% backlog, ${district.venomPercent}% highly venomous mix and ${district.loadIndex || 1}x average rescuer load.`,
            escalation: anomalyScore >= 2
              ? "Escalate to state-level monitoring and active redeployment."
              : "Keep district under enhanced operational watch.",
          };
        })
        .filter((district) => district.anomalyScore >= 1.2 || district.severityLabel !== "Watch")
        .sort((a, b) => b.anomalyScore - a.anomalyScore);
    },

    deploymentRecommendations() {
      const shortage = (this.predictiveData && this.predictiveData.shortage) || [];
      const recommendations = shortage.slice(0, 3).map((district, index) => ({
        id: `shortage-${district.district}-${index}`,
        title: district.district,
        subtitle: `${district.total.toLocaleString()} incidents · ${district.activeRescuers} active rescuers · ${district.rescuersNeeded} more needed`,
        action: district.rescuerAdvice,
        priority: district.riskLevel === "CRITICAL DEFICIT" ? "Critical" : district.riskLevel === "HIGH DEFICIT" ? "High" : "Priority",
        color: district.riskLevel === "CRITICAL DEFICIT" ? "red darken-2" : district.riskLevel === "HIGH DEFICIT" ? "deep-orange darken-2" : "amber darken-3",
      }));

      if (this.activeTemporalFrame && this.activeTemporalFrame.primaryDistrict) {
        recommendations.unshift({
          id: `outlook-${this.activeTemporalFrame.id}`,
          title: `${this.activeTemporalFrame.primaryDistrict} hotspot surge`,
          subtitle: `${this.activeTemporalFrame.hotspotCount} projected hotspot cell(s) in ${this.activeTemporalFrame.label}`,
          action: `Stage rapid response teams for ${this.activeTemporalFrame.topSpecies} encounters before the projected ${this.activeTemporalFrame.topIntensity}% intensity window begins.`,
          priority: this.activeTemporalFrame.topIntensity >= 80 ? "Critical" : "High",
          color: this.activeTemporalFrame.topIntensity >= 80 ? "red darken-2" : "blue darken-2",
        });
      }

      return recommendations;
    },

    aiOpsAlerts() {
      const alerts = [];
      this.deploymentRecommendations.slice(0, 2).forEach((item) => {
        alerts.push({
          id: item.id,
          type: "deployment",
          label: `${item.title} deploy`,
          color: item.color,
          district: item.title,
        });
      });
      this.backlogRiskDistricts.slice(0, 2).forEach((item) => {
        alerts.push({
          id: `backlog-${item.district}`,
          type: "backlog",
          label: `${item.district} backlog`,
          color: item.breachRisk >= 75 ? "deep-orange darken-2" : "amber darken-3",
          district: item.district,
        });
      });
      this.anomalyDistricts.slice(0, 2).forEach((item) => {
        alerts.push({
          id: `anomaly-${item.district}`,
          type: "anomaly",
          label: `${item.district} anomaly`,
          color: item.severityColor,
          district: item.district,
        });
      });
      return alerts;
    },

    whatIfProjection() {
      if (!this.selectedSimulationDistrict || !this.predictiveData || !this.predictiveData.districts) return null;

      const district = this.predictiveData.districts.find((item) => item.district === this.selectedSimulationDistrict);
      if (!district) return null;

      const extra = Number(this.simulationExtraRescuers || 0);
      const currentRescuers = Math.max(0, district.activeRescuers || 0);
      const projectedRescuers = currentRescuers + extra;
      const benchmark = (this.predictiveData.totals && this.predictiveData.totals.benchmarkIncidentsPerRescuer) || 0;
      const currentLoad = district.incidentsPerRescuer || district.total || 0;
      const projectedLoad = projectedRescuers > 0 ? Number((district.total / projectedRescuers).toFixed(1)) : currentLoad;
      const projectedLoadIndex = benchmark > 0 ? Number((projectedLoad / benchmark).toFixed(2)) : 0;
      const baseEta = district.avgArrivalMins != null ? district.avgArrivalMins : 25;
      const projectedEta = projectedRescuers > 0 && currentRescuers > 0
        ? Math.max(12, Math.round(baseEta * Math.pow(currentRescuers / projectedRescuers, 0.65)))
        : baseEta;
      const backlogMultiplier = currentLoad > 0 ? projectedLoad / currentLoad : 1;
      const projectedBacklog = Math.max(0, Math.round((district.backlog || 0) * Math.max(0.3, backlogMultiplier)));
      const remainingNeed = benchmark > 0 ? Math.max(0, Math.ceil(district.total / benchmark) - projectedRescuers) : 0;

      return {
        projectedEta,
        projectedBacklog,
        projectedLoadIndex,
        remainingNeed,
        summary: `Adding ${extra} rescuer(s) in ${district.district} would move load from ${currentLoad}/rescuer to ${projectedLoad}/rescuer and reduce modeled response delay from ${baseEta} to ${projectedEta} minutes.`,
      };
    },

    selectionNarrative() {
      if (this.isSelectionActive && this.selectedRecords.length > 0) {
        const topSpecies = this.selectionStats[0] ? this.selectionStats[0].name : "Unknown species";
        const venomousCount = this.selectedRecords.filter((record) => {
          const vType = String((record.snake && record.snake.venomType) || "").toLowerCase();
          const sName = String((record.snake && record.snake.name) || "").toLowerCase();
          return vType.includes("high") || HIGHLY_VENOMOUS_SPECIES.has(sName);
        }).length;
        const venomRatio = Math.round((venomousCount / Math.max(1, this.selectedRecords.length)) * 100);
        return `The selected polygon contains ${this.selectedRecords.length} incidents. ${topSpecies} is the dominant species, and about ${venomRatio}% of the selected encounters are highly venomous, so this zone should be treated as a priority response pocket if field teams are limited.`;
      }

      if (this.predictiveData && this.predictiveData.shortage && this.predictiveData.shortage.length) {
        const top = this.predictiveData.shortage[0];
        const species = this.predictiveData.species && this.predictiveData.species[0] ? this.predictiveData.species[0].name : "snake encounters";
        return `${top.district} is the strongest current intervention zone, carrying ${top.total.toLocaleString()} incidents with ${top.activeRescuers} active rescuers. Statewide, ${species} is the most common encounter signature in the current window, which means deployment should prioritize both load balancing and species-specific preparedness.`;
      }

      return "AI narrative will appear here once predictive analytics or a map selection is available.";
    },

    operationalPatternClusters() {
      const districts = (this.predictiveData && this.predictiveData.districts) || [];
      if (!districts.length) return [];

      const highBacklog = districts.filter((district) => district.backlogRate >= 20).map((district) => district.district);
      const venomPressure = districts.filter((district) => district.venomPercent >= 30).map((district) => district.district);
      const fastResponse = districts.filter((district) => district.avgArrivalMins != null && district.avgArrivalMins <= 20).map((district) => district.district);
      const loadStress = districts.filter((district) => (district.loadIndex || 1) >= 1.5).map((district) => district.district);

      return [
        {
          label: "Backlog Pressure Cluster",
          detail: highBacklog.length ? `${highBacklog.join(", ")} are carrying the heaviest unresolved queue pressure.` : "No district is currently showing severe backlog accumulation.",
        },
        {
          label: "High-Venom Exposure Cluster",
          detail: venomPressure.length ? `${venomPressure.join(", ")} show the strongest highly venomous encounter mix.` : "Highly venomous encounter mix is currently moderate across districts.",
        },
        {
          label: "Fast SLA Coverage Cluster",
          detail: fastResponse.length ? `${fastResponse.join(", ")} are maintaining sub-20-minute field arrival.` : "No district is consistently holding sub-20-minute arrival at the moment.",
        },
        {
          label: "Rescuer Load Stress Cluster",
          detail: loadStress.length ? `${loadStress.join(", ")} are above 1.5x the state-average rescuer load.` : "No district is currently above 1.5x the state-average rescuer load.",
        },
      ];
    },

    repeatConflictPredictions() {
      const districts = (this.predictiveData && this.predictiveData.districts) || [];
      return districts
        .map((district) => {
          const repeatRisk = Math.min(
            95,
            Math.round(
              Math.min(40, district.stateShare || 0) +
              Math.min(25, district.backlogRate || 0) +
              Math.min(20, district.venomPercent || 0) +
              Math.max(0, ((district.loadIndex || 1) - 1) * 18)
            )
          );
          return {
            district: district.district,
            repeatRisk,
            reason: `${district.topSpecies} remains dominant here, with ${district.total.toLocaleString()} incidents and ${district.backlogRate}% backlog indicating recurrent public conflict potential.`,
          };
        })
        .sort((a, b) => b.repeatRisk - a.repeatRisk);
    },
  },
  watch: {
    selectedSeason() {
      // Season changes re-query the backend (shortage / species reflect the season).
      this.fetchPredictive();
    },
    appLoading: {
      immediate: true,
      handler(val) {
        if (val) {
          this.startLoadingTicker();
        } else {
          this.stopLoadingTicker();
        }
      },
    },
  },
  created() {
    this.mapObject = null;
    this.drawnItems = null;
    this.markersClusterGroup = null;
    this.heatmapLayer = null;
    this.pointersLayer = null;
    this.drawControl = null;
    this.spatialLayerGroup = null;
    this.backlogRiskLayerGroup = null;
    this.anomalyRiskLayerGroup = null;
    this.temporalOutlookLayerGroup = null;
  },
  beforeMount() {
    this.getData();
    this.getList();
    this.getDivisionData();
    this.find();
  },
  mounted() {
    this.initMap();
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.onFullscreenChange);
    document.addEventListener("mozfullscreenchange", this.onFullscreenChange);
    document.addEventListener("MSFullscreenChange", this.onFullscreenChange);
  },
  beforeDestroy() {
    this.stopLoadingTicker();
    this.stopTemporalPlayback();
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.onFullscreenChange);
    document.removeEventListener("mozfullscreenchange", this.onFullscreenChange);
    document.removeEventListener("MSFullscreenChange", this.onFullscreenChange);
    if (this.mapObject) {
      this.mapObject.remove();
      this.mapObject = null;
    }
  },
  methods: {
    normalizeDistrictName(name) {
      const raw = String(name || "").trim().toLowerCase();
      return DISTRICT_ALIASES[raw] || raw;
    },

    getDistrictCenter(districtName) {
      const normalized = this.normalizeDistrictName(districtName);
      const matchKey = Object.keys(DISTRICT_COORDS).find(
        (key) => normalized.includes(key) || key.includes(normalized)
      );
      return matchKey ? DISTRICT_COORDS[matchKey] : [10.8505, 76.2711];
    },

    getDistrictMetrics(districtName) {
      const districts = (this.predictiveData && this.predictiveData.districts) || [];
      const normalized = this.normalizeDistrictName(districtName);
      return districts.find((district) => {
        const districtKey = this.normalizeDistrictName(district.district);
        return districtKey.includes(normalized) || normalized.includes(districtKey);
      }) || null;
    },

    haversineKm(lat1, lon1, lat2, lon2) {
      const toRad = (value) => (value * Math.PI) / 180;
      const earthRadiusKm = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    resolveEscalationSuggestion({ backlogRate = 0, avgArrivalMins = null, venomPercent = 0, loadIndex = 1 }) {
      if ((avgArrivalMins != null && avgArrivalMins > 35) || backlogRate >= 25 || loadIndex >= 2 || venomPercent >= 45) {
        return "Escalate to district control room and pre-position additional RRT coverage";
      }
      if ((avgArrivalMins != null && avgArrivalMins > 25) || backlogRate >= 15 || loadIndex >= 1.5 || venomPercent >= 30) {
        return "Keep on active watch and prepare reinforcements";
      }
      return "Local team monitoring is sufficient";
    },

    clearLeafletLayerGroup(layerGroup, removeFromMap = false) {
      if (!layerGroup) return;
      try {
        layerGroup.clearLayers();
      } catch (error) {
        console.warn("Layer group clear failed:", error);
      }
      if (removeFromMap && this.mapObject) {
        try {
          this.mapObject.removeLayer(layerGroup);
        } catch (error) {
          console.warn("Layer group remove failed:", error);
        }
      }
    },

    // Fetch measured predictive analytics from the backend (real SLA, backlog,
    // rescuer workload, species/venom mix, and a seasonal-trend forecast).
    fetchPredictive() {
      const params = {};
      if (this.timeperiod && this.fromdate && this.todate) {
        params.from = this.fromdate;
        params.to = this.todate;
      }
      if (this.district && this.district !== "All") params.district = this.district;
      if (this.selectedSeason) params.season = this.selectedSeason;

      this.predictiveLoading = true;
      axios({
        method: "POST",
        url: "/location/predictive",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: params,
      })
        .then((response) => {
          if (response.data && response.data.status === true) {
            this.predictiveData = response.data.data;
            if (
              !this.selectedSimulationDistrict ||
              !response.data.data.districts.some((district) => district.district === this.selectedSimulationDistrict)
            ) {
              this.selectedSimulationDistrict = response.data.data.districts.length
                ? response.data.data.districts[0].district
                : "";
            }
            // Refresh the shortage overlay if it is currently shown.
            if (this.showShortageMapOverlay) this.toggleShortageLayer();
            if (this.showBacklogOverlay) this.renderBacklogRiskOverlay();
            if (this.showAnomalyOverlay) this.renderAnomalyRiskOverlay();
          } else if (response.data) {
            this.msg = response.data.msg || "Could not load predictive analytics.";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          console.error("predictive fetch error:", err);
        })
        .finally(() => {
          this.predictiveLoading = false;
        });
    },

    formatHour(h) {
      if (h === null || h === undefined) return "Any time";
      const ampm = h < 12 ? "AM" : "PM";
      const hr = h % 12 === 0 ? 12 : h % 12;
      const next = (h + 1) % 24;
      const ampm2 = next < 12 ? "AM" : "PM";
      const hr2 = next % 12 === 0 ? 12 : next % 12;
      return `${hr} ${ampm} – ${hr2} ${ampm2}`;
    },

    // Map an intensity (0..1) to a blue→green→amber→red colour.
    intensityColor(t) {
      const v = Math.max(0, Math.min(1, Number(t) || 0));
      if (v < 0.33) return "#2563eb";      // blue - low
      if (v < 0.6) return "#22c55e";       // green - moderate
      if (v < 0.8) return "#f59e0b";       // amber - high
      return "#dc2626";                    // red - very high
    },

    // Fetch the spatiotemporal encounter forecast and plot it on the map.
    fetchSpatialForecast() {
      const params = { month: this.forecastMonth };
      if (this.forecastHour !== null && this.forecastHour !== undefined) params.hour = this.forecastHour;
      if (this.forecastSpecies) params.snakeid = [this.forecastSpecies];

      this.spatialForecastLoading = true;
      axios({
        method: "POST",
        url: "/location/spatial-forecast",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: params,
      })
        .then((response) => {
          if (response.data && response.data.status === true) {
            this.spatialForecastData = response.data.data;
            this.plotSpatialForecast();
            if (!this.spatialForecastData.cells.length) {
              this.msg = "No historical encounters match this month/time/species combination.";
              this.showSnackBar = true;
            }
          } else if (response.data) {
            this.msg = response.data.msg || "Could not generate spatial forecast.";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          console.error("spatial forecast error:", err);
          this.msg = "Failed to generate spatial forecast.";
          this.showSnackBar = true;
        })
        .finally(() => {
          this.spatialForecastLoading = false;
        });
    },

    plotSpatialForecast() {
      if (!this.mapObject || !this.spatialForecastData) return;

      if (!this.spatialLayerGroup) {
        this.spatialLayerGroup = L.layerGroup().addTo(this.mapObject);
      } else {
        try { this.spatialLayerGroup.clearLayers(); } catch (err) { console.warn(err); }
        if (!this.mapObject.hasLayer(this.spatialLayerGroup)) this.mapObject.addLayer(this.spatialLayerGroup);
      }

      const data = this.spatialForecastData;
      const boundsCoords = [];

      data.cells.forEach((cell) => {
        const color = this.intensityColor(cell.intensity);
        const likelihood = Math.round(cell.intensity * 100);
        boundsCoords.push([cell.lat, cell.lon]);

        const circle = L.circleMarker([cell.lat, cell.lon], {
          radius: 6 + cell.intensity * 12,
          color: "#ffffff",
          weight: 1,
          fillColor: color,
          fillOpacity: 0.55,
        });

        const hourText = data.hour !== null ? ` around ${this.formatHour(data.hour)}` : "";
        circle.bindPopup(`
          <div style="font-family: sans-serif; padding: 6px; min-width: 210px;">
            <div style="font-size: 13px; font-weight: 900; color: ${color}; margin-bottom: 4px;">
              🔮 ${likelihood}% relative likelihood
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 2px;">
              <strong>When:</strong> ${data.monthLabel}${hourText}
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 2px;">
              <strong>Where:</strong> ${cell.lat.toFixed(3)}, ${cell.lon.toFixed(3)}
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 2px;">
              <strong>Expected:</strong> ~${cell.perYear} encounters/yr
            </div>
            <div style="font-size: 11px; color: #334155;">
              <strong>Most likely species:</strong> ${cell.topSpecies}
            </div>
          </div>
        `);
        this.spatialLayerGroup.addLayer(circle);
      });

      if (boundsCoords.length > 0) {
        const bounds = L.latLngBounds(boundsCoords);
        if (bounds.isValid()) this.mapObject.fitBounds(bounds, { padding: [50, 50], maxZoom: 11 });
      }
    },

    clearSpatialForecast() {
      this.spatialForecastData = null;
      if (this.spatialLayerGroup && this.mapObject) {
        try {
          this.spatialLayerGroup.clearLayers();
          this.mapObject.removeLayer(this.spatialLayerGroup);
        } catch (err) { console.warn(err); }
      }
    },

    toggleBacklogRiskOverlay() {
      if (this.showBacklogOverlay) {
        this.renderBacklogRiskOverlay();
      } else {
        this.clearLeafletLayerGroup(this.backlogRiskLayerGroup, true);
      }
    },

    renderBacklogRiskOverlay() {
      if (!this.mapObject) return;
      if (!this.backlogRiskLayerGroup) {
        this.backlogRiskLayerGroup = L.layerGroup().addTo(this.mapObject);
      } else {
        this.clearLeafletLayerGroup(this.backlogRiskLayerGroup);
        this.mapObject.addLayer(this.backlogRiskLayerGroup);
      }

      this.backlogRiskDistricts.slice(0, 8).forEach((district) => {
        const [lat, lon] = this.getDistrictCenter(district.district);
        const circle = L.circle([lat, lon], {
          radius: 7000 + district.breachRisk * 70,
          color: district.breachRisk >= 75 ? "#c2410c" : "#f59e0b",
          fillColor: district.breachRisk >= 75 ? "#fb923c" : "#fbbf24",
          fillOpacity: 0.18,
          weight: 2,
        });
        circle.bindPopup(`
          <div style="font-family:sans-serif; min-width:220px;">
            <div style="font-weight:900; color:#c2410c; margin-bottom:4px;">${district.district} backlog breach watch</div>
            <div style="font-size:11px; color:#334155;">Risk score: ${district.breachRisk}%</div>
            <div style="font-size:11px; color:#334155;">Backlog: ${district.backlog} (${district.backlogRate}%)</div>
            <div style="font-size:11px; color:#334155;">Average arrival: ${district.avgArrivalMinsLabel}</div>
          </div>
        `);
        this.backlogRiskLayerGroup.addLayer(circle);
      });
    },

    toggleAnomalyRiskOverlay() {
      if (this.showAnomalyOverlay) {
        this.renderAnomalyRiskOverlay();
      } else {
        this.clearLeafletLayerGroup(this.anomalyRiskLayerGroup, true);
      }
    },

    renderAnomalyRiskOverlay() {
      if (!this.mapObject) return;
      if (!this.anomalyRiskLayerGroup) {
        this.anomalyRiskLayerGroup = L.layerGroup().addTo(this.mapObject);
      } else {
        this.clearLeafletLayerGroup(this.anomalyRiskLayerGroup);
        this.mapObject.addLayer(this.anomalyRiskLayerGroup);
      }

      this.anomalyDistricts.slice(0, 8).forEach((district) => {
        const [lat, lon] = this.getDistrictCenter(district.district);
        const marker = L.circleMarker([lat, lon], {
          radius: Math.min(16, 8 + district.anomalyScore * 2),
          color: "#ffffff",
          weight: 1.5,
          fillColor: district.severityLabel === "Escalate" ? "#6b21a8" : "#9333ea",
          fillOpacity: 0.85,
        });
        marker.bindPopup(`
          <div style="font-family:sans-serif; min-width:220px;">
            <div style="font-weight:900; color:#6b21a8; margin-bottom:4px;">${district.district} anomaly watch</div>
            <div style="font-size:11px; color:#334155;">Severity: ${district.severityLabel}</div>
            <div style="font-size:11px; color:#334155;">${district.reason}</div>
            <div style="font-size:11px; color:#4c1d95; margin-top:4px;">${district.escalation}</div>
          </div>
        `);
        this.anomalyRiskLayerGroup.addLayer(marker);
      });
    },

    buildClickedMapInsight(lat, lon) {
      const districts = (this.predictiveData && this.predictiveData.districts) || [];
      let nearestDistrict = null;
      let minDistrictDistance = Infinity;

      districts.forEach((district) => {
        const [dLat, dLon] = this.getDistrictCenter(district.district);
        const distance = this.haversineKm(lat, lon, dLat, dLon);
        if (distance < minDistrictDistance) {
          minDistrictDistance = distance;
          nearestDistrict = district;
        }
      });

      const forecastCells = (this.activeTemporalFrame && this.activeTemporalFrame.cells && this.activeTemporalFrame.cells.length)
        ? this.activeTemporalFrame.cells
        : (this.spatialForecastData && this.spatialForecastData.cells) || [];
      let nearestCell = null;
      let minCellDistance = Infinity;

      forecastCells.slice(0, 120).forEach((cell) => {
        const distance = this.haversineKm(lat, lon, cell.lat, cell.lon);
        if (distance < minCellDistance) {
          minCellDistance = distance;
          nearestCell = cell;
        }
      });

      const topSpecies = nearestCell ? nearestCell.topSpecies : (nearestDistrict ? nearestDistrict.topSpecies : "Unknown");
      const venomPercent = nearestDistrict ? nearestDistrict.venomPercent || 0 : 0;
      const intensityBoost = nearestCell ? Math.round((nearestCell.intensity || 0) * 40) : 0;
      const threatScore = Math.min(100, Math.round(venomPercent * 0.65 + intensityBoost + Math.max(0, ((nearestDistrict && nearestDistrict.loadIndex) || 1) - 1) * 15));

      let threatLevel = "Moderate";
      if (threatScore >= 75) threatLevel = "High";
      else if (threatScore <= 35) threatLevel = "Low";

      const baseEta = nearestDistrict && nearestDistrict.avgArrivalMins != null ? nearestDistrict.avgArrivalMins : 24;
      const distanceFactor = nearestDistrict ? Math.min(1.35, 1 + (minDistrictDistance / 55)) : 1;
      const etaMinutes = Math.round(baseEta * distanceFactor);
      const confidence = Math.min(
        95,
        Math.round(
          nearestCell
            ? 45 + (nearestCell.intensity || 0) * 35 + Math.min(15, (nearestCell.perYear || 0) * 4)
            : 40 + Math.min(30, venomPercent / 2)
        )
      );
      const escalation = this.resolveEscalationSuggestion({
        backlogRate: nearestDistrict ? nearestDistrict.backlogRate : 0,
        avgArrivalMins: nearestDistrict ? nearestDistrict.avgArrivalMins : null,
        venomPercent,
        loadIndex: nearestDistrict ? nearestDistrict.loadIndex : 1,
      });

      return {
        district: nearestDistrict ? nearestDistrict.district : "Kerala",
        coordsLabel: `${lat.toFixed(3)}, ${lon.toFixed(3)}`,
        topSpecies,
        threatLevel,
        etaMinutes,
        confidence,
        escalation,
        deploymentRecommendation: nearestDistrict && nearestDistrict.rescuersNeeded > 0
          ? `Shift at least +${nearestDistrict.rescuersNeeded} rescuers toward ${nearestDistrict.district} if this hotspot becomes active.`
          : "Keep the current district team on alert and monitor repeat sightings.",
        narrative: nearestCell
          ? `Nearest modeled hotspot favors ${topSpecies} with ${Math.round((nearestCell.intensity || 0) * 100)}% relative likelihood.`
          : `No direct hotspot cell was found for this point, so intelligence is inferred from district-level encounter and rescuer-load patterns.`,
      };
    },

    handleMapClick(event) {
      if (!event || !event.latlng) return;
      this.clickedMapLatLng = { lat: event.latlng.lat, lon: event.latlng.lng };
      this.clickedMapInsight = this.buildClickedMapInsight(event.latlng.lat, event.latlng.lng);
      this.activePanel = "ops";
    },

    async generateTemporalOutlook() {
      this.temporalOutlookLoading = true;
      this.stopTemporalPlayback();

      try {
        const stepHours = this.temporalOutlookHorizon <= 6 ? 2 : 3;
        const offsets = [];
        for (let offset = 0; offset <= this.temporalOutlookHorizon; offset += stepHours) {
          offsets.push(offset);
        }

        const responses = await Promise.all(
          offsets.map(async (offset) => {
            const future = new Date(Date.now() + offset * 60 * 60 * 1000);
            const payload = {
              month: future.getMonth() + 1,
              hour: future.getHours(),
            };
            if (this.forecastSpecies) payload.snakeid = [this.forecastSpecies];

            const response = await axios({
              method: "POST",
              url: "/location/spatial-forecast",
              headers: { "x-auth-token": localStorage.getItem("token") },
              data: payload,
            });

            if (!response.data || response.data.status !== true) return null;
            const data = response.data.data;
            const topCell = data.cells && data.cells.length ? data.cells[0] : null;
            const topDistrict = topCell ? this.buildClickedMapInsight(topCell.lat, topCell.lon).district : "Kerala";
            const topIntensity = topCell ? Math.round((topCell.intensity || 0) * 100) : 0;
            const confidence = Math.min(95, topCell ? Math.round(45 + (topCell.intensity || 0) * 35 + Math.min(15, (topCell.perYear || 0) * 4)) : 25);

            return {
              id: `outlook-${offset}-${payload.month}-${payload.hour}`,
              offset,
              month: payload.month,
              hour: payload.hour,
              label: `${future.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · ${this.formatHour(payload.hour)}`,
              summary: topCell
                ? `Primary pressure projects near ${topDistrict}, led by ${topCell.topSpecies} and ~${topCell.perYear}/year equivalent encounter density.`
                : "No strong hotspot signature for this forecast frame.",
              cells: data.cells || [],
              topSpecies: topCell ? topCell.topSpecies : "No hotspot",
              topIntensity,
              confidence,
              hotspotCount: data.cells ? data.cells.length : 0,
              primaryDistrict: topDistrict,
            };
          })
        );

        this.temporalOutlookFrames = responses.filter(Boolean);
        this.temporalOutlookIndex = 0;

        if (this.temporalOutlookFrames.length) {
          this.renderTemporalOutlookFrame();
          this.msg = `Generated ${this.temporalOutlookFrames.length} AI outlook frame(s) for the next ${this.temporalOutlookHorizon} hours.`;
        } else {
          this.clearTemporalOutlook();
          this.msg = "No matching temporal hotspot outlook could be generated.";
        }
        this.showSnackBar = true;
      } catch (error) {
        console.error("temporal outlook generation error:", error);
        this.msg = "Failed to generate temporal AI outlook.";
        this.showSnackBar = true;
      } finally {
        this.temporalOutlookLoading = false;
      }
    },

    renderTemporalOutlookFrame() {
      const frame = this.activeTemporalFrame;
      if (!frame || !this.mapObject) return;

      if (!this.temporalOutlookLayerGroup) {
        this.temporalOutlookLayerGroup = L.layerGroup().addTo(this.mapObject);
      } else {
        this.clearLeafletLayerGroup(this.temporalOutlookLayerGroup);
        this.mapObject.addLayer(this.temporalOutlookLayerGroup);
      }

      const boundsCoords = [];
      frame.cells.slice(0, 120).forEach((cell) => {
        boundsCoords.push([cell.lat, cell.lon]);
        const color = this.intensityColor(cell.intensity || 0);
        const marker = L.circleMarker([cell.lat, cell.lon], {
          radius: 5 + (cell.intensity || 0) * 11,
          color: "#ffffff",
          weight: 1,
          fillColor: color,
          fillOpacity: 0.45,
        });
        marker.bindPopup(`
          <div style="font-family:sans-serif; min-width:220px;">
            <div style="font-weight:900; color:${color}; margin-bottom:4px;">${frame.label}</div>
            <div style="font-size:11px; color:#334155;">${cell.topSpecies}</div>
            <div style="font-size:11px; color:#334155;">Intensity: ${Math.round((cell.intensity || 0) * 100)}%</div>
            <div style="font-size:11px; color:#334155;">Expected: ~${cell.perYear}/yr</div>
          </div>
        `);
        this.temporalOutlookLayerGroup.addLayer(marker);
      });

      if (boundsCoords.length) {
        const bounds = L.latLngBounds(boundsCoords);
        if (bounds.isValid()) {
          this.mapObject.fitBounds(bounds, { padding: [45, 45], maxZoom: 11 });
        }
      }
    },

    selectTemporalOutlookFrame(frameIndex) {
      this.temporalOutlookIndex = frameIndex;
      this.renderTemporalOutlookFrame();
    },

    startTemporalPlayback() {
      if (this.temporalOutlookFrames.length < 2) return;
      this.stopTemporalPlayback();
      this.temporalOutlookPlaying = true;
      this.temporalOutlookTimer = setInterval(() => {
        this.temporalOutlookIndex = (this.temporalOutlookIndex + 1) % this.temporalOutlookFrames.length;
        this.renderTemporalOutlookFrame();
      }, 1700);
    },

    stopTemporalPlayback() {
      if (this.temporalOutlookTimer) {
        clearInterval(this.temporalOutlookTimer);
        this.temporalOutlookTimer = null;
      }
      this.temporalOutlookPlaying = false;
    },

    toggleTemporalPlayback() {
      if (this.temporalOutlookPlaying) {
        this.stopTemporalPlayback();
      } else {
        this.startTemporalPlayback();
      }
    },

    clearTemporalOutlook() {
      this.stopTemporalPlayback();
      this.temporalOutlookFrames = [];
      this.temporalOutlookIndex = 0;
      this.clearLeafletLayerGroup(this.temporalOutlookLayerGroup, true);
    },

    focusAiAlert(alert) {
      if (!alert) return;
      if (alert.type === "backlog") {
        this.showBacklogOverlay = true;
        this.renderBacklogRiskOverlay();
      } else if (alert.type === "anomaly") {
        this.showAnomalyOverlay = true;
        this.renderAnomalyRiskOverlay();
      }

      const district = alert.district;
      if (district && this.mapObject) {
        const [lat, lon] = this.getDistrictCenter(district);
        this.mapObject.flyTo([lat, lon], 10, { duration: 1.2 });
      }
    },

    flyToCell(cell) {
      if (this.mapObject && cell) {
        this.mapObject.flyTo([cell.lat, cell.lon], 12, { duration: 1.2 });
      }
    },

    runDistrictAIQuery(districtName) {
      if (!districtName) return;
      const normName = districtName.trim().toLowerCase();
      const d = this.predictiveData;

      const dist = d && d.districts
        ? d.districts.find((x) => {
            const dn = x.district.toLowerCase();
            return dn.includes(normName) || normName.includes(dn);
          })
        : null;

      if (!dist) {
        this.aiQueryResult = null;
        this.msg = `No rescue records found for ${districtName} in the current window.`;
        this.showSnackBar = true;
        return;
      }

      const avgResp = dist.avgArrivalMins != null ? dist.avgArrivalMins : "N/A";

      let shortageStatus = "Optimal Coverage";
      if (dist.riskLevel === "CRITICAL DEFICIT") shortageStatus = "Critical Deficit";
      else if (dist.riskLevel === "HIGH DEFICIT") shortageStatus = "High Deficit";
      else if (dist.riskLevel === "MODERATE DEFICIT") shortageStatus = "Moderate Deficit";

      let rescuerFormula;
      if (dist.rescuersNeeded > 0) {
        rescuerFormula =
          `${dist.district} has ${dist.activeRescuers} active rescuer(s) handling ${dist.total.toLocaleString()} incidents` +
          (dist.incidentsPerRescuer != null ? ` (${dist.incidentsPerRescuer} incidents/rescuer` : "") +
          (dist.loadIndex != null ? `, ${dist.loadIndex}× the state average)` : dist.incidentsPerRescuer != null ? ")" : "") +
          `. Balancing this district to the state-average load would need about +${dist.rescuersNeeded} rescuer(s) ` +
          `(target total: ${dist.activeRescuers + dist.rescuersNeeded}).`;
      } else {
        rescuerFormula =
          `${dist.district} has ${dist.activeRescuers} active rescuer(s) handling ${dist.total.toLocaleString()} incidents` +
          (dist.loadIndex != null ? ` (${dist.loadIndex}× the state-average load)` : "") +
          `. Rescuer allocation is balanced; no redeployment required.`;
      }

      this.aiQueryResult = {
        districtName: dist.district,
        totalCalls: dist.total,
        stateShare: dist.stateShare,
        avgResponseMins: avgResp,
        slaStatus: dist.slaStatus,
        unattendedCount: dist.backlog,
        unattendedRate: dist.backlogRate,
        venomPercent: dist.venomPercent,
        rescuerFormula,
        shortageNeeded: dist.rescuersNeeded,
        shortageStatus,
        rescuerAdvice: dist.rescuerAdvice,
        speciesChances: (dist.speciesChances || []).slice(0, 5),
        districtAreasBreakdown: [],
      };

      const DISTRICT_COORDS = {
        kannur: [11.8745, 75.3704],
        ernakulam: [9.9816, 76.2999],
        kozhikode: [11.2588, 75.7804],
        thiruvananthapuram: [8.5241, 76.9366],
        kottayam: [9.5916, 76.5222],
        malappuram: [11.0720, 76.0740],
        thrissur: [10.5276, 76.2144],
        palakkad: [10.7867, 76.6548],
        wayanad: [11.6854, 76.1320],
        idukki: [9.8494, 76.9806],
        pathanamthitta: [9.2648, 76.7870],
        alappuzha: [9.4981, 76.3388],
        kollam: [8.8932, 76.6141],
        kasaragod: [12.5102, 74.9852],
      };
      const key = Object.keys(DISTRICT_COORDS).find((k) => normName.includes(k) || dist.district.toLowerCase().includes(k));
      if (key && this.mapObject) {
        this.mapObject.flyTo(DISTRICT_COORDS[key], 11, { duration: 1.5 });
      }
    },

    handleAIQuerySubmit() {
      if (!this.aiQueryInputText) return;
      const text = this.aiQueryInputText.toLowerCase();

      const districts = [
        "thiruvananthapuram", "kollam", "pathanamthitta", "alappuzha",
        "kottayam", "idukki", "ernakulam", "thrissur", "palakkad",
        "malappuram", "kozhikode", "wayanad", "kannur", "kasaragod",
        "trivandrum", "cochin", "kochi", "calicut", "cannanore"
      ];

      const aliasMap = {
        trivandrum: "thiruvananthapuram",
        cochin: "ernakulam",
        kochi: "ernakulam",
        calicut: "kozhikode",
        cannanore: "kannur"
      };

      let found = districts.find((d) => text.includes(d));
      if (found) {
        if (aliasMap[found]) found = aliasMap[found];
        this.runDistrictAIQuery(found);
      } else {
        this.runDistrictAIQuery("kozhikode");
      }
    },

    toggleShortageMapOverlay() {
      this.showShortageMapOverlay = !this.showShortageMapOverlay;
      if (!this.predictiveData) {
        this.fetchPredictive();
      }
      this.toggleShortageLayer();
    },

    toggleShortageLayer() {
      if (!this.mapObject) return;

      if (!this.shortageLayerGroup) {
        this.shortageLayerGroup = L.layerGroup().addTo(this.mapObject);
      } else {
        try {
          this.shortageLayerGroup.clearLayers();
        } catch (err) {
          console.warn("Error clearing shortageLayerGroup:", err);
        }
        this.mapObject.addLayer(this.shortageLayerGroup);
      }

      if (!this.showShortageMapOverlay) {
        if (this.shortageLayerGroup) {
          try {
            this.mapObject.removeLayer(this.shortageLayerGroup);
          } catch (err) {
            console.warn("Error removing shortageLayerGroup:", err);
          }
        }
        return;
      }

      const shortageItems = this.deepPredictiveStats.shortageDivisions || [];

      if (!shortageItems || shortageItems.length === 0) {
        this.msg = "No shortage zones calculated for the current dataset.";
        this.showSnackBar = true;
        return;
      }

      const DISTRICT_COORDS = {
        kannur: [11.8745, 75.3704],
        ernakulam: [9.9816, 76.2999],
        kozhikode: [11.2588, 75.7804],
        thiruvananthapuram: [8.5241, 76.9366],
        kottayam: [9.5916, 76.5222],
        malappuram: [11.0720, 76.0740],
        thrissur: [10.5276, 76.2144],
        palakkad: [10.7867, 76.6548],
        wayanad: [11.6854, 76.1320],
        idukki: [9.8494, 76.9806],
        pathanamthitta: [9.2648, 76.7870],
        alappuzha: [9.4981, 76.3388],
        kollam: [8.8932, 76.6141],
        kasaragod: [12.5102, 74.9852],
      };

      const boundsCoords = [];

      shortageItems.forEach((item, idx) => {
        const key = Object.keys(DISTRICT_COORDS).find((k) => item.name.toLowerCase().includes(k));
        const baseCoords = key ? DISTRICT_COORDS[key] : [10.8505, 76.2711];
        const latLng = [baseCoords[0] + ((idx % 3 - 1) * 0.12), baseCoords[1] + ((Math.floor(idx / 3) - 1) * 0.12)];

        boundsCoords.push(latLng);

        const circle = L.circle(latLng, {
          radius: 12000,
          color: "#dc2626",
          fillColor: "#ef4444",
          fillOpacity: 0.25,
          weight: 2.5,
          dashArray: "5,5",
        });

        const customIcon = L.divIcon({
          className: "custom-scarcity-pin",
          html: `
            <div style="background:#dc2626; color:#ffffff; font-family:sans-serif; padding:4px 8px; border-radius:12px; font-size:10px; font-weight:900; box-shadow:0 4px 12px rgba(220,38,38,0.5); border:2px solid #ffffff; white-space:nowrap; display:flex; align-items:center; gap:4px;">
              <span>🚨 ${item.name}</span>
              <span style="background:#ffffff; color:#dc2626; padding:1px 5px; border-radius:8px;">+${item.needed} NEEDED</span>
            </div>
          `,
          iconSize: [130, 24],
          iconAnchor: [65, 12],
        });

        const marker = L.marker(latLng, { icon: customIcon });

        const popupContent = `
          <div style="font-family: sans-serif; padding: 6px; min-width: 220px;">
            <div style="font-size: 13px; font-weight: 900; color: #dc2626; margin-bottom: 4px;">
              🚨 ${item.name} Load-Imbalance Alert
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
              <strong>Active Season:</strong> ${this.getSeasonLabel(this.selectedSeason)}
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
              <strong>Encounter Load:</strong> ${item.count.toLocaleString()} incidents
            </div>
            <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
              <strong>Active Team:</strong> ${item.activeRescuers} rescuers (${item.incidentsPerRescuer} incidents/rescuer${item.loadIndex ? `, ${item.loadIndex}× state avg` : ""})
            </div>
            <div style="font-size: 11px; font-weight: 800; color: #dc2626; margin-top: 6px; padding: 4px; background: #fee2e2; border-radius: 4px;">
              🎯 To balance against state-average load: +${item.needed} rescuer(s)
            </div>
          </div>
        `;

        marker.bindPopup(popupContent);
        circle.bindPopup(popupContent);

        this.shortageLayerGroup.addLayer(circle);
        this.shortageLayerGroup.addLayer(marker);
      });

      if (boundsCoords.length > 0) {
        const bounds = L.latLngBounds(boundsCoords);
        if (bounds.isValid()) {
          this.mapObject.fitBounds(bounds, { padding: [60, 60], maxZoom: 10 });
        }
      }

      this.msg = `Displaying ${shortageItems.length} Rescuer Scarcity Zones for ${this.getSeasonLabel(this.selectedSeason)}`;
      this.showSnackBar = true;
    },

    async waitForMapTiles(timeout = 4000) {
      const mapNode = this.$refs.mapContainer || (this.mapObject && this.mapObject.getContainer());
      if (!mapNode) return;

      const tiles = Array.from(mapNode.querySelectorAll(".leaflet-tile"));
      if (tiles.length === 0) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        return;
      }

      await Promise.race([
        Promise.all(
          tiles.map(
            (tile) =>
              new Promise((resolve) => {
                if (tile.complete && tile.naturalWidth > 0) {
                  resolve();
                  return;
                }
                tile.addEventListener("load", resolve, { once: true });
                tile.addEventListener("error", resolve, { once: true });
              })
          )
        ),
        new Promise((resolve) => setTimeout(resolve, timeout)),
      ]);
    },

    async buildMapImageExport() {
      if (!this.mapObject) {
        throw new Error("Map is not ready for export.");
      }

      this.mapObject.invalidateSize();
      await this.waitForMapTiles();
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const mapNode = this.$refs.mapContainer || this.mapObject.getContainer();
      if (!mapNode) {
        throw new Error("Map container not found.");
      }

      const rect = mapNode.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = await html2canvas(mapNode, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        scale,
        width,
        height,
        imageTimeout: 8000,
        ignoreElements: (element) => {
          if (!element || !element.classList) return false;
          return (
            element.classList.contains("leaflet-control-container") ||
            element.classList.contains("leaflet-popup-pane") ||
            element.classList.contains("leaflet-tooltip-pane")
          );
        },
        onclone: (clonedDocument) => {
          const clonedMap = clonedDocument.getElementById("map");
          if (!clonedMap) return;

          clonedMap.style.background = "#ffffff";
          clonedMap.querySelectorAll(".leaflet-control-container, .leaflet-popup-pane, .leaflet-tooltip-pane").forEach((node) => {
            node.remove();
          });
        },
      });

      return {
        dataUrl: canvas.toDataURL("image/png"),
        width,
        height,
      };
    },

    downloadDataUrl(filename, url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    getExportFileBase() {
      const dateStr = new Date().toISOString().slice(0, 10);
      return `SARPA_Snake_Rescued_Map_${dateStr}`;
    },

    async withExportLoading(callback) {
      if (this.exportLoading) return;

      this.exportLoading = true;
      try {
        await callback();
      } catch (error) {
        console.error("Map export failed:", error);
        this.msg = "Map export failed. Please try again.";
        this.showSnackBar = true;
      } finally {
        this.exportLoading = false;
      }
    },

    exportMapPDF() {
      this.withExportLoading(async () => {
        this.msg = "Preparing map-only PDF export...";
        this.showSnackBar = true;

        const { dataUrl, width, height } = await this.buildMapImageExport();
        const exportWindow = window.open("", "_blank", "noopener,noreferrer,width=1200,height=900");
        if (!exportWindow) {
          throw new Error("Popup blocked");
        }

        const orientation = width > height ? "landscape" : "portrait";
        exportWindow.document.write(`
          <html>
            <head>
              <title>${this.getExportFileBase()}</title>
              <style>
                @page { size: ${orientation}; margin: 10mm; }
                html, body {
                  margin: 0;
                  padding: 0;
                  background: #ffffff;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                img {
                  width: 100%;
                  height: auto;
                  display: block;
                }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" alt="Snake rescued map export" />
            </body>
          </html>
        `);
        exportWindow.document.close();
        exportWindow.focus();
        setTimeout(() => {
          exportWindow.print();
        }, 400);
      });
    },

    exportMapPNG() {
      this.withExportLoading(async () => {
        this.msg = "Preparing map-only PNG export...";
        this.showSnackBar = true;
        const { dataUrl } = await this.buildMapImageExport();
        this.downloadDataUrl(`${this.getExportFileBase()}.png`, dataUrl);
      });
    },

    exportMapExcel() {
      this.withExportLoading(async () => {
        const records = this.isSelectionActive && this.selectedRecords.length > 0
          ? this.selectedRecords
          : this.data;

        const tableRows = records
          .map((item, index) => {
            const snake = item.snake && item.snake.name ? item.snake.name : "Unknown";
            const district = item.district || item.locationname || "-";
            const division = item.division || "-";
            const range = item.range || "-";
            const status = item.status || (item.rescue && item.rescue.status) || "-";
            const rescuer = item.user && (item.user.name || item.user.username)
              ? (item.user.name || item.user.username)
              : "-";
            const latitude = Array.isArray(item.location) ? item.location[1] : item.latitude || item.lat || "-";
            const longitude = Array.isArray(item.location) ? item.location[0] : item.longitude || item.lon || "-";

            return `
              <tr>
                <td>${index + 1}</td>
                <td>${this.formatDate(item.createdAt || item.date || item.rescuedate || "")}</td>
                <td>${snake}</td>
                <td>${district}</td>
                <td>${division}</td>
                <td>${range}</td>
                <td>${status}</td>
                <td>${rescuer}</td>
                <td>${latitude}</td>
                <td>${longitude}</td>
              </tr>
            `;
          })
          .join("");

        const html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
            <head>
              <meta charset="utf-8" />
              <style>
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #cbd5e1; padding: 8px; font-size: 12px; }
                th { background: #dcfce7; font-weight: 700; }
              </style>
            </head>
            <body>
              <table>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Date</th>
                    <th>Snake</th>
                    <th>District</th>
                    <th>Division</th>
                    <th>Range</th>
                    <th>Status</th>
                    <th>Rescuer</th>
                    <th>Latitude</th>
                    <th>Longitude</th>
                  </tr>
                </thead>
                <tbody>${tableRows}</tbody>
              </table>
            </body>
          </html>
        `;

        const blob = new Blob([html], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
        this.downloadDataUrl(`${this.getExportFileBase()}.xls`, url);
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        this.msg = "Excel export generated from the current map dataset.";
        this.showSnackBar = true;
      });
    },

    getSeasonLabel(season) {
      if (season === "monsoon") return "South-West Monsoon";
      if (season === "post_monsoon") return "Post-Monsoon Breeding Window";
      if (season === "summer") return "Summer Dry Season";
      if (season === "current") return "Current Month Window";
      return "All-Year Window";
    },

    startLoadingTicker() {
      this.stopLoadingTicker();
      this.loadingMsgIndex = 0;
      this.loadingTimer = setInterval(() => {
        this.loadingMsgIndex = (this.loadingMsgIndex + 1) % this.loadingMessages.length;
      }, 700);
    },

    stopLoadingTicker() {
      if (this.loadingTimer) {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
      }
    },
    initMap() {
      this.$nextTick(() => {
        const mapDom = document.getElementById("map");
        if (!mapDom) return;

        if (this.mapObject) {
          this.mapObject.remove();
          this.mapObject = null;
        }

        this.mapObject = L.map("map", {
          center: [10.8505, 76.2711],
          zoom: 7.5,
          zoomControl: false,
          scrollWheelZoom: false,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          crossOrigin: true,
        }).addTo(this.mapObject);

        L.control.zoom({ position: "bottomright" }).addTo(this.mapObject);

        this.drawnItems = new L.FeatureGroup();
        this.mapObject.addLayer(this.drawnItems);

        this.markersClusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 45,
          chunkedLoading: true,
          chunkInterval: 5000,
          chunkDelay: 1,
        });

        this.pointersLayer = L.layerGroup();

        this.drawControl = new L.Control.Draw({
          position: "topright",
          draw: {
            polygon: {
              allowIntersection: false,
              showArea: true,
              shapeOptions: { color: "#0f766e", fillColor: "#0f766e", fillOpacity: 0.2, weight: 2 },
            },
            rectangle: {
              shapeOptions: { color: "#0f766e", fillColor: "#0f766e", fillOpacity: 0.2, weight: 2 },
            },
            circle: false, polyline: false, marker: false, circlemarker: false,
          },
          edit: { featureGroup: this.drawnItems, remove: true },
        });

        if (this.showDrawControls) {
          this.mapObject.addControl(this.drawControl);
        }

        this.mapObject.on(L.Draw.Event.CREATED, (e) => {
          const layer = e.layer;
          this.drawnItems.clearLayers();
          this.drawnItems.addLayer(layer);
          this.processSelection(layer);
        });

        this.mapObject.on(L.Draw.Event.DELETED, () => {
          this.clearSelection();
        });

        this.mapObject.on("click", this.handleMapClick);

      });
    },

    renderMapData() {
      if (!this.mapObject) {
        setTimeout(() => {
          this.renderMapData();
        }, 100);
        return;
      }

      // Ensure layer groups are instantiated safely
      if (!this.markersClusterGroup) {
        this.markersClusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 45,
          chunkedLoading: true,
          chunkInterval: 5000,
          chunkDelay: 1,
        });
      } else {
        try {
          this.markersClusterGroup.clearLayers();
        } catch (err) {
          console.warn("Failed clearing markersClusterGroup:", err);
        }
      }

      if (!this.pointersLayer) {
        this.pointersLayer = L.layerGroup();
      } else {
        try {
          this.pointersLayer.clearLayers();
        } catch (err) {
          console.warn("Failed clearing pointersLayer:", err);
        }
      }

      if (this.heatmapLayer) {
        try {
          this.mapObject.removeLayer(this.heatmapLayer);
        } catch (err) {
          console.warn("Failed removing heatmapLayer:", err);
        }
        this.heatmapLayer = null;
      }

      try {
        if (this.mapObject.hasLayer(this.markersClusterGroup)) {
          this.mapObject.removeLayer(this.markersClusterGroup);
        }
        if (this.mapObject.hasLayer(this.pointersLayer)) {
          this.mapObject.removeLayer(this.pointersLayer);
        }
      } catch (err) {
        console.warn("Failed removing layers from map:", err);
      }

      if (!this.data || this.data.length === 0) {
        if (this.hasFetchedRecords) {
          this.appLoading = false;
        }
        return;
      }

      try {
        const canvasRenderer = L.canvas({ padding: 0.5 });
        const heatmapPoints = [];
        const pointerBatch = [];
        const clusterBatch = [];

        this.data.forEach((user) => {
          let lat = null;
          let lon = null;

          if (user.location) {
            if (Array.isArray(user.location) && user.location.length >= 2) {
              lon = Number(user.location[0]);
              lat = Number(user.location[1]);
            } else if (typeof user.location === "object" && Array.isArray(user.location.coordinates) && user.location.coordinates.length >= 2) {
              lon = Number(user.location.coordinates[0]);
              lat = Number(user.location.coordinates[1]);
            }
          }

          if (lat === null || lon === null || isNaN(lat) || isNaN(lon) || lat === 0 || lon === 0) {
            if (user.lat && user.lon) {
              lat = Number(user.lat);
              lon = Number(user.lon);
            } else if (user.latitude && user.longitude) {
              lat = Number(user.latitude);
              lon = Number(user.longitude);
            }
          }

          if (lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) {
            heatmapPoints.push([lat, lon, 1.0]);

            const sName = String((user.snake && user.snake.name) || "").toLowerCase();
            const vType = String((user.snake && user.snake.venomType) || "").toLowerCase();
            const isVenomous = vType.includes("highly") || vType.includes("high") || HIGHLY_VENOMOUS_SPECIES.has(sName);
            const riskScore = isVenomous ? 92 : 35;

            if (this.showPointers) {
              const marker = L.circleMarker([lat, lon], {
                renderer: canvasRenderer,
                radius: 6,
                fillColor: isVenomous ? "#e11d48" : "#059669",
                color: "#ffffff",
                weight: 1,
                opacity: 0.9,
                fillOpacity: 0.8,
              });

              marker.on("click", (e) => {
                if (e && e.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
                this.selectedPointer = { ...user, isVenomous, riskScore };
              });
              pointerBatch.push(marker);
            }

            if (this.showHotspots) {
              const clusterMarker = L.marker([lat, lon]);
              clusterMarker.on("click", (e) => {
                if (e && e.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
                this.selectedPointer = { ...user, isVenomous, riskScore };
              });
              clusterBatch.push(clusterMarker);
            }
          }
        });

        if (this.showPointers && pointerBatch.length > 0) {
          pointerBatch.forEach((m) => this.pointersLayer.addLayer(m));
          this.mapObject.addLayer(this.pointersLayer);
        }

        if (this.showHotspots && clusterBatch.length > 0) {
          this.markersClusterGroup.addLayers(clusterBatch);
          this.mapObject.addLayer(this.markersClusterGroup);
        }

        if (this.showHeatmap && heatmapPoints.length > 0) {
          this.heatmapLayer = L.heatLayer(heatmapPoints, {
            radius: 25,
            blur: 15,
            maxZoom: 16,
          }).addTo(this.mapObject);
        }

        this.calculateSelectionStats();
      } catch (err) {
        console.error("Error rendering map data:", err);
      } finally {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.appLoading = false;
            }, 400);
          });
        });
      }
    },

    toggleMapMode() {
      this.renderMapData();
    },

    togglePanel(panelName) {
      this.activePanel = this.activePanel === panelName ? null : panelName;
    },

    toggleDrawControls() {
      this.showDrawControls = !this.showDrawControls;
    },

    // GIS Layer Toggle Helpers
    toggleDistrictLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.districts) {
        if (!this.geoJsonData.districts) {
          axios.get("/alldata/formap", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.districts = res.data.data;
              this.geoJsonLeafletLayers.districts = L.geoJSON(res.data.data, {
                style: { color: "#1e3a8a", weight: 2, fillOpacity: 0.05, dashArray: "4,4" },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.districts) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.districts);
        }
      } else if (this.geoJsonLeafletLayers.districts) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.districts);
      }
    },
    toggleDivisionLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.divisions) {
        if (!this.geoJsonData.divisions) {
          axios.get("/shapes/divisions", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.divisions = res.data.data;
              this.geoJsonLeafletLayers.divisions = L.geoJSON(res.data.data, {
                style: { color: "#047857", weight: 1.5, fillOpacity: 0.08 },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.divisions) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.divisions);
        }
      } else if (this.geoJsonLeafletLayers.divisions) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.divisions);
      }
    },
    toggleRangeLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.ranges) {
        if (!this.geoJsonData.ranges) {
          axios.get("/shapes/ranges", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.ranges = res.data.data;
              this.geoJsonLeafletLayers.ranges = L.geoJSON(res.data.data, {
                style: { color: "#b45309", weight: 1.2, fillOpacity: 0.04 },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.ranges) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.ranges);
        }
      } else if (this.geoJsonLeafletLayers.ranges) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.ranges);
      }
    },
    togglePASLayer() {
      if (!this.mapObject) return;
      if (this.gisLayers.pas) {
        if (!this.geoJsonData.pas) {
          axios.get("/shapes/pas/", { headers: { "x-auth-token": localStorage.getItem("token") } }).then((res) => {
            if (res.data && res.data.status) {
              this.geoJsonData.pas = res.data.data;
              this.geoJsonLeafletLayers.pas = L.geoJSON(res.data.data, {
                style: { color: "#7e22ce", weight: 2, fillOpacity: 0.15 },
              }).addTo(this.mapObject);
            }
          });
        } else if (this.geoJsonLeafletLayers.pas) {
          this.mapObject.addLayer(this.geoJsonLeafletLayers.pas);
        }
      } else if (this.geoJsonLeafletLayers.pas) {
        this.mapObject.removeLayer(this.geoJsonLeafletLayers.pas);
      }
    },

    toggleFullscreen() {
      const elem = this.$el.querySelector(".snake-map-dashboard") || this.$el;
      if (!elem) return;

      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (elem.requestFullscreen) elem.requestFullscreen();
        else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      this.$nextTick(() => {
        if (this.mapObject) {
          setTimeout(() => this.mapObject.invalidateSize(), 150);
        }
      });
    },

    processSelection(layer) {
      const latlngs = layer.getLatLngs()[0];
      const selected = [];

      this.data.forEach((item) => {
        if (item.location && item.location.length === 2) {
          const lat = item.location[1];
          const lon = item.location[0];
          if (this.isPointInPolygon([lat, lon], latlngs)) {
            selected.push(item);
          }
        }
      });

      this.selectedRecords = selected;
      this.isSelectionActive = true;
      this.calculateSelectionStats();
    },

    isPointInPolygon(point, polygonCoords) {
      const x = point[0];
      const y = point[1];
      let inside = false;

      for (let i = 0, j = polygonCoords.length - 1; i < polygonCoords.length; j = i++) {
        const xi = polygonCoords[i].lat;
        const yi = polygonCoords[i].lng;
        const xj = polygonCoords[j].lat;
        const yj = polygonCoords[j].lng;

        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    },

    clearSelection() {
      this.selectedRecords = [];
      this.isSelectionActive = false;
      if (this.drawnItems) {
        this.drawnItems.clearLayers();
      }
      this.calculateSelectionStats();
    },

    calculateSelectionStats() {
      const records = this.isSelectionActive ? this.selectedRecords : this.data;

      const speciesMap = {};
      records.forEach((item) => {
        const rawName = item.snake && item.snake.name ? item.snake.name.trim() : "";
        if (rawName && !rawName.toLowerCase().includes("unidentified") && !rawName.toLowerCase().includes("unknown")) {
          speciesMap[rawName] = (speciesMap[rawName] || 0) + 1;
        }
      });

      this.selectionStats = Object.keys(speciesMap)
        .map((name) => ({ name, count: speciesMap[name] }))
        .sort((a, b) => b.count - a.count);
    },

    getSpeciesPercentage(count) {
      const statsArray = this.selectionStats;
      if (!statsArray || statsArray.length === 0) return 0;
      const maxCount = statsArray[0].count;
      return maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
    },

    save(fromdate) {
      this.fromdate = fromdate;
      this.menu = false;
    },
    saveto(todate) {
      this.todate = todate;
      this.menu2 = false;
    },

    getDivisionData() {
      axios({
        method: "GET",
        url: "/platform/division/getlistdropdown",
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.divisionList = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    getRangeData() {
      axios({
        method: "POST",
        url: "/platform/range/getlistdropdown",
        headers: { token: localStorage.getItem("token") },
        data: { division: this.divisionname },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.rangeList = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    checkUpdate() {
      if (this.alldata === true) {
        this.timeperiod = false;
        this.bigsnakes = false;
        this.allsnakes = false;
        this.divisioncheck = false;
        this.users = false;
        this.districtcheck = false;

        const { from, to } = this.getDefaultDates();
        this.fromdate = from;
        this.todate = to;

        this.district = "";
        this.bigsnakeselect = [];
        this.allsnakelist = [];
        this.selecteduser = "";
        this.divisionname = "";
        this.rangename = "";

        this.find();
      }
    },

    reset() {
      this.timeperiod = false;
      this.bigsnakes = false;
      this.allsnakes = false;
      this.users = false;
      this.divisioncheck = false;
      this.districtcheck = false;
      this.alldata = true;
      this.checkUpdate();
    },

    getDefaultDates() {
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      return {
        from: sixMonthsAgo.toISOString().substring(0, 10),
        to: today.toISOString().substring(0, 10),
      };
    },

    getData() {
      axios({
        method: "GET",
        url: "/user/rescued/all",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { keyword: this.searchUser },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.userslist = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    find() {
      const params = {};
      if (this.timeperiod && this.fromdate && this.todate) {
        params.from = this.fromdate;
        params.to = this.todate;
      }
      if (this.bigsnakeselect && this.bigsnakeselect.length > 0) params.big4 = this.bigsnakeselect;
      if (this.allsnakelist && this.allsnakelist.length > 0) params.snakeid = this.allsnakelist;
      if (this.selecteduser) params.username = this.selecteduser;
      if (this.divisionname) params.division = this.divisionname;
      if (this.rangename) params.range = this.rangename;
      if (this.district) params.district = this.district;

      this.appLoading = true;
      this.hasFetchedRecords = false;
      axios({
        method: "POST",
        url: "/location/find",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: params,
      })
        .then((response) => {
          if (response.data.status === true) {
            this.data = response.data.data.records || [];
            this.totalcount = this.data.length;
            this.hasFetchedRecords = true;
            this.fetchPredictive();
            this.clearSelection();
            this.renderMapData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.hasFetchedRecords = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.hasFetchedRecords = true;
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    formatDate(date) {
      if (!date) return "-";
      const [year, month, day] = date.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },

    getList() {
      axios({
        method: "GET",
        url: "/snake/all/",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.snakeitems = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
.snake-map-dashboard {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #0f172a;
}

#map {
  width: 100%;
  height: 100%;
  z-index: 1;
}

.floating-title-banner {
  position: absolute;
  top: 18px;
  left: 20px;
  z-index: 999;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 118, 110, 0.2);
  border-radius: 16px;
  padding: 10px 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.banner-main-title {
  font-size: 11px;
  font-weight: 800;
  color: #0f766e;
  letter-spacing: 0.1em;
}

.banner-subtitle {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.floating-north-arrow {
  position: absolute;
  top: 18px;
  right: 20px;
  z-index: 999;
}

.map-floating-toolbar {
  position: absolute;
  top: 90px;
  left: 20px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toolbar-btn {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
}

.floating-overlay-card {
  position: absolute;
  top: 90px;
  left: 76px;
  z-index: 999;
  width: 380px;
  max-width: calc(100vw - 100px);
  max-height: calc(100vh - 180px);
  background: rgba(255, 255, 255, 0.96) !important;
  backdrop-filter: blur(12px);
  border-radius: 20px !important;
  border: 1px solid rgba(15, 118, 110, 0.2);
  padding: 18px;
  display: flex;
  flex-direction: column;
}

.panel-card-title {
  font-size: 16px;
  font-weight: 800;
}

.panel-card-subtitle {
  font-size: 11px;
  color: #64748b;
}

.stats-scroll-container {
  overflow-y: auto;
  max-height: 480px;
}

/* Predictive Cards & Metrics */
.predictive-metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.metric-box {
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
}

.bg-teal-light { background: #ccfbf1; }
.bg-rose-light { background: #ffe4e6; }
.bg-amber-light { background: #fef3c7; }
.bg-blue-light { background: #dbeafe; }

.metric-box__label {
  font-size: 10px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
}

.metric-box__val {
  font-size: 20px;
  font-weight: 900;
  line-height: 1.1;
  margin-top: 2px;
}

.ai-field-recommendation {
  background: #f0fdf4;
  border: 1px solid #a7f3d0;
}

/* Floating Pointer Inspector Drawer */
.predictive-pointer-inspector {
  position: absolute;
  bottom: 24px;
  left: 20px;
  z-index: 999;
  width: 420px;
  max-width: calc(100vw - 40px);
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(15, 118, 110, 0.2);
  border-radius: 20px;
  padding: 14px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.16);
}

.inspector-img-wrap {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.inspector-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.inspector-img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inspector-name {
  font-size: 15px;
  font-weight: 800;
}

/* Timeline Floating Card */
.floating-timeline-card {
  position: absolute;
  bottom: 24px;
  right: 20px;
  z-index: 999;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 118, 110, 0.2);
  border-radius: 16px;
  padding: 10px 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.timeline-title {
  font-size: 10px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
}

.timeline-dates {
  font-size: 13px;
  font-weight: 800;
  color: #0f766e;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.25s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(-15px);
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.25s ease;
}
.slide-up-enter, .slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* ANALYTICS LOADING OVERLAY STYLES */
.analytics-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999999;
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.analytics-loader-card {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(15, 118, 110, 0.25);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35), 0 0 30px rgba(15, 118, 110, 0.15);
  border-radius: 20px;
  padding: 36px 32px;
  max-width: 440px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.radar-pulse-container {
  position: relative;
  width: 90px;
  height: 90px;
  margin: 0 auto 20px auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-center-icon {
  z-index: 2;
  background: #f0fdf4;
  border-radius: 50%;
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #0f766e;
  box-shadow: 0 0 15px rgba(15, 118, 110, 0.3);
}

.radar-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid #0f766e;
  opacity: 0;
  animation: radarPulse 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

.ring-1 { width: 60px; height: 60px; animation-delay: 0s; }
.ring-2 { width: 80px; height: 80px; animation-delay: 0.5s; }
.ring-3 { width: 100px; height: 100px; animation-delay: 1s; }

@keyframes radarPulse {
  0% { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; }
}

.loader-heading {
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 1.5px;
  color: #0f766e;
  margin-bottom: 6px;
}

.loader-subtext {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 20px;
}

.loader-progress-wrap {
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
  position: relative;
}

.loader-progress-bar {
  height: 100%;
  width: 45%;
  background: linear-gradient(90deg, #0f766e, #06b6d4, #10b981);
  border-radius: 10px;
  animation: progressSlide 1.6s infinite ease-in-out;
}

@keyframes progressSlide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

.loader-status-ticker {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
}

.spinning-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

.loader-footer-hint {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.floating-scarcity-btn-container {
  position: absolute;
  top: 16px;
  right: 64px;
  z-index: 1000;
}

@keyframes scarcityPulse {
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
  70% { box-shadow: 0 0 0 12px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
}

.pulsate-scarcity {
  animation: scarcityPulse 1.8s infinite;
}

@media print {
  body, html {
    background: #ffffff !important;
  }
  .map-floating-toolbar,
  .floating-title-banner,
  .floating-north-arrow,
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-btn,
  .v-snack,
  .leaflet-control-container {
    display: none !important;
  }
  .snake-map-dashboard {
    height: 100vh !important;
    width: 100vw !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    background: #ffffff !important;
    z-index: 99999 !important;
  }
  .floating-overlay-card {
    position: relative !important;
    top: auto !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    max-height: none !important;
    box-shadow: none !important;
    border: 1px solid #94a3b8 !important;
    page-break-inside: avoid;
  }
}
</style>
